import {
  areHiddenColumnsEqual,
  areSortSettingsEqual,
  arrayMove,
  calculateFrozenColumnWidths,
  calculateHiddenColumnsFromParent,
  convertToApiColour,
  convertToApiFilter,
  convertToApiRowLock,
  isColumnLockedByRules,
  getColumnsPerUserRole,
  getMissingColumns,
  getMissingColumnsFromParentView,
  getRowValue,
  mapUnknownFilterTypes,
  mergeColumnValuesCount
} from 'components/spreadsheet/helpers/functions'
import {
  ISpreadsheetData,
  sortDirections,
  ICellUpdateAction,
  SpreadsheetReducerActions,
  IColumnValuesCount
} from 'components/spreadsheet/types'
import {
  ITableColumn,
  ITableRow,
  ITableViewColumn,
  ITableViewSort,
  ITableViewFilter,
  ICommentThreadStats,
  EditorContent,
  ICellValue,
  ITableViewWithColumns,
  ITableViewGroup,
  ITableViewHiddenColumn,
  ITable,
  ITableViewColour,
  ITableViewRowLock,
  ITableViewChart,
  ITransposeSettings,
  IMatrixSettings
} from 'types'
import { areColourSettingsEqual } from 'components/spreadsheet/helpers/colouring'
import { convertCachedCellValue } from 'components/spreadsheet/helpers/columnKindChange'
import { areRowLockSettingsEqual } from 'components/spreadsheet/helpers/locking'
import { areFilterSettingsEqual } from 'components/spreadsheet/helpers/filtering'
import { areGroupSettingsEqual, getGroupingKeysForColumn } from 'components/spreadsheet/helpers/grouping'
import {
  BLOCK_SEARCH_BY,
  EMPTY_MATRIX_SETTINGS,
  EMPTY_TRANSPOSE_SETTINGS,
  INITIAL_SELECTED_SEARCH_CELL,
  viewSupportsGrouping
} from 'components/spreadsheet/constants/const'
import { extractRowIdFromPivotColumnId, FIELD_COLUMN_ID } from 'components/spreadsheet/helpers/transpose'
import { ITableViewHistory } from './actions'
import {
  getCaseInsensitiveDuplicateColumnIds,
  getDuplicateValueKey,
  isCaseInsensitiveDuplicateKind
} from 'components/spreadsheet/helpers/duplicates'

// Email columns tally duplicates case-insensitively. The per-row loops below would otherwise
// rescan the column list for every value they count, so build the id set once. Call sites gate
// this on having tracked columns, so tables without duplicate validation never scan.
const NO_CASE_INSENSITIVE_COLUMNS: ReadonlySet<string> = new Set<string>()

const caseInsensitiveDuplicateColumns = (state: ISpreadsheetData): Set<string> =>
  getCaseInsensitiveDuplicateColumnIds(state.viewDetails?.columns, state.tableDetails?.columns)

const changeColumnWidth = (state: ISpreadsheetData, columnId: string, columnWidth: number) => {
  let newColumns = [...state.viewDetails.columns]

  newColumns = newColumns.map((column: ITableViewColumn) => {
    if (column.publicId === columnId) return { ...column, width: columnWidth }
    else return { ...column }
  })

  const newColumnWidths = calculateFrozenColumnWidths(
    newColumns,
    state.viewDetails.frozenIndex,
    state.userConfiguration.hiddenColumns
  )
  return { ...state, columnWidths: newColumnWidths, viewDetails: { ...state.viewDetails, columns: newColumns } }
}

const changeMatrixColumnWidth = (state: ISpreadsheetData, columnId: string, width: number) => {
  const previous = state.viewDetails.matrixSettings ?? EMPTY_MATRIX_SETTINGS
  // Matrix synthetic IDs (row-label and data-cell columns alike) are stable
  // strings — store widths against them directly. Mirrors the transpose
  // pattern but uses one map for both column families since matrix has N
  // row-label columns, not a single "field" column.
  const dataColumnWidths = { ...(previous.dataColumnWidths ?? {}), [columnId]: width }
  const matrixSettings: IMatrixSettings = { ...previous, dataColumnWidths }
  return {
    ...state,
    viewDetails: { ...state.viewDetails, matrixSettings },
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeTransposeColumnWidth = (state: ISpreadsheetData, columnId: string, width: number) => {
  const previous: ITransposeSettings = state.viewDetails.transposeSettings ?? EMPTY_TRANSPOSE_SETTINGS

  let transposeSettings: ITransposeSettings
  if (columnId === FIELD_COLUMN_ID) {
    transposeSettings = { ...previous, fieldColumnWidth: width }
  } else {
    // Synthetic data column IDs are `__pivot_<rowPublicId>__`; key the per-row
    // width map by that publicId so each column persists its width independently.
    const rowPublicId = extractRowIdFromPivotColumnId(columnId)
    const dataColumnWidths = { ...(previous.dataColumnWidths ?? {}), [rowPublicId]: width }
    transposeSettings = { ...previous, dataColumnWidths }
  }

  return {
    ...state,
    viewDetails: { ...state.viewDetails, transposeSettings },
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeColumnAggregate = (state: ISpreadsheetData, columnId: string, aggregate: number) => {
  let newColumns = [...state.viewDetails.columns]

  newColumns = newColumns.map((column: ITableViewColumn) => {
    if (column.publicId === columnId) return { ...column, aggregate }
    else return { ...column }
  })

  return { ...state, viewDetails: { ...state.viewDetails, columns: newColumns } }
}

const createTableColumn = (state: ISpreadsheetData, column: ITableViewColumn) => {
  const newColumns = [...state.viewDetails.columns]
  newColumns.splice(column.sortOrder, 0, column)
  newColumns.forEach((column, index) => (column.sortOrder = index))
  const newColumnWidths = calculateFrozenColumnWidths(
    newColumns,
    state.viewDetails.frozenIndex,
    state.userConfiguration.hiddenColumns
  )

  // add column to default and current view, hide all others
  const newTableView = [...state.tableView].map((view: ITableViewWithColumns) => {
    const newView = { ...view }
    if (newView.hiddenColumns && view.publicId !== state.activeTableView && !view.isDefault) {
      newView.hiddenColumns.push({ columnId: column.publicId, columnName: column.name })
    } else {
      newView.columns.push(column)
    }
    return newView
  })

  // Add new column to column values count
  const newColumnValuesCount = { ...state.columnValuesCount }
  newColumnValuesCount[column.publicId] = {}

  // Update columnIdToIndex and indexToColumnId for the new column
  const newColumnIdToIndex = new Map(state.columnIdToIndex)
  const newIndexToColumnId = [...state.indexToColumnId]
  const newColumnIndex = newIndexToColumnId.length
  newColumnIdToIndex.set(column.publicId, newColumnIndex)
  newIndexToColumnId.push(column.publicId)

  // Extend all row rowData arrays with null for the new column
  if (state.columnStore) {
    state.columnStore.addColumn(newColumnIndex, column.kind)
  }
  const newRows = state.columnStore
    ? state.rows
    : state.rows.map((row) => ({
        ...row,
        rowData: [...(row.rowData ?? []), null]
      }))

  return {
    ...state,
    tableDetails: {
      ...state.tableDetails,
      columns: [
        ...state.tableDetails.columns,
        {
          aggregate: column.aggregate,
          description: column.description,
          isIndexed: column.isIndexed,
          isJoined: column.isJoined,
          kind: column.kind,
          name: column.name,
          publicId: column.publicId,
          script: column.script,
          scriptEnabled: column.scriptEnabled,
          formula: column.formula,
          formulaEnabled: column.formulaEnabled,
          width: column.width,
          thousandSeparator: column.thousandSeparator,
          displayLink: column.displayLink,
          decimalPlaces: column.decimalPlaces,
          dateFormat: column.dateFormat,
          defaultValue: column.defaultValue,
          headerBackgroundColor: column.headerBackgroundColor,
          headerTextColor: column.headerTextColor,
          exportWidth: column.exportWidth,
          isSynced: column.isSynced
        }
      ]
    },
    refreshing: false,
    columnWidths: newColumnWidths,
    tableView: newTableView,
    columnValuesCount: newColumnValuesCount,
    viewDetails: { ...state.viewDetails, columns: newColumns },
    rows: newRows,
    columnIdToIndex: newColumnIdToIndex,
    indexToColumnId: newIndexToColumnId
  }
}

/** Strip a deleted column's references out of a matrix-view settings blob.
 *  Used by ``deleteTableColumn`` so matrix views don't end up pointing at
 *  columns that no longer exist on the table (which would make
 *  ``getMatrixData`` return EMPTY_RESULT and the view fall back to a
 *  grouped spreadsheet render). */
const cleanMatrixSettings = (
  matrixSettings: IMatrixSettings | null | undefined,
  columnId: string
): IMatrixSettings | null | undefined => {
  if (!matrixSettings) return matrixSettings
  // Synthetic width keys end with ``::<columnId>`` for both data-cell columns
  // (``matrix::<b64>::<columnId>``) and row-label columns (``matrix-row-label::<columnId>``).
  const cleanedWidths = matrixSettings.dataColumnWidths
    ? Object.fromEntries(
        Object.entries(matrixSettings.dataColumnWidths).filter(([key]) => !key.endsWith(`::${columnId}`))
      )
    : matrixSettings.dataColumnWidths
  return {
    ...matrixSettings,
    pivotColumnId: matrixSettings.pivotColumnId === columnId ? null : matrixSettings.pivotColumnId,
    rowLabelColumnIds: matrixSettings.rowLabelColumnIds.filter((id) => id !== columnId),
    dataFieldColumnIds: matrixSettings.dataFieldColumnIds.filter((id) => id !== columnId),
    dataColumnWidths: cleanedWidths
  }
}

// Delete one or more columns in a single, atomic state transition. Doing every removed column
// in one pass (rather than dispatching DELETE_COLUMN N times) means the expensive work — remapping
// column indices and stripping each column out of every row's rowData / the column store — happens
// once, not once per column.
const deleteTableColumns = (state: ISpreadsheetData, columnIds: string[]) => {
  if (columnIds.length === 0) return state
  const removeSet = new Set(columnIds)

  const cleanMatrixForRemovedColumns = (
    matrixSettings: ITableViewWithColumns['matrixSettings']
  ): ITableViewWithColumns['matrixSettings'] => {
    let cleaned = matrixSettings
    for (const columnId of columnIds) {
      cleaned = cleanMatrixSettings(cleaned, columnId) ?? null
    }
    return cleaned
  }

  // Delete columns from view (and re-index the survivors' sortOrder)
  const newColumnsView = [...state.viewDetails.columns].filter(
    (column: ITableViewColumn) => !removeSet.has(column.publicId)
  )
  newColumnsView.forEach((column, index) => (column.sortOrder = index))

  // delete columns from table
  const newColumnsTable = [...state.tableDetails.columns].filter(
    (column: ITableColumn) => !removeSet.has(column.publicId)
  )

  // delete columns from all views
  const newColumnsTableView = [...state.tableView].map((view: ITableViewWithColumns) => {
    const newView = { ...view }
    newView.columns = view.columns.filter((column) => !removeSet.has(column.publicId))
    newView.filterSettings = view.filterSettings.filter((setting: ITableViewFilter) => !removeSet.has(setting.columnId))
    newView.sortSettings = view.sortSettings.filter((setting: ITableViewSort) => !removeSet.has(setting.columnId))
    newView.groupSettings = view.groupSettings.filter((setting: ITableViewGroup) => !removeSet.has(setting.columnId))
    newView.colourSettings = view.colourSettings.filter((setting: ITableViewColour) => !removeSet.has(setting.columnId))
    newView.rowLockSettings = view.rowLockSettings.filter(
      (setting: ITableViewRowLock) => !removeSet.has(setting.columnId)
    )
    newView.hiddenColumns =
      view.hiddenColumns &&
      view.hiddenColumns.filter((column: ITableViewHiddenColumn) => !removeSet.has(column.columnId))
    newView.matrixSettings = cleanMatrixForRemovedColumns(view.matrixSettings)
    return newView
  })

  // remove columns from hidden columns if present
  const newHiddenColumns = [...state.userConfiguration.hiddenColumns].filter(
    (hiddenColumnId: string) => !removeSet.has(hiddenColumnId)
  )

  // remove columns from sorting settings if present
  const newSortSettings = [...state.userConfiguration.sortSettings].filter(
    (sortSetting) => !removeSet.has(sortSetting.columnId)
  )

  // remove columns from filter settings if present
  const newFilterSettings = [...state.userConfiguration.filterSettings].filter(
    (filterSetting) => !removeSet.has(filterSetting.columnId)
  )
  // remove columns from grouping settings if present
  const newGroupSettings = [...state.userConfiguration.groupSettings].filter(
    (groupSetting) => !removeSet.has(groupSetting.columnId)
  )
  // remove columns from colour settings if present
  const newColourSettings = [...state.userConfiguration.colourSettings].filter(
    (colourSetting) => !removeSet.has(colourSetting.columnId)
  )

  // remove columns from row-lock settings if present
  const newRowLockSettings = [...state.userConfiguration.rowLockSettings].filter(
    (lockSetting) => !removeSet.has(lockSetting.columnId)
  )

  // remove columns from data joins if present
  const newJoins = state.tableDetails.joins.map((join) => ({
    ...join,
    dataColumns: join.dataColumns.filter((dataColumn) => !removeSet.has(dataColumn.targetColumnId))
  }))

  // remove the columns from column values count
  const newColumnValuesCount = { ...state.columnValuesCount }
  columnIds.forEach((columnId) => delete newColumnValuesCount[columnId])

  // Rebuild columnIdToIndex and indexToColumnId without the deleted columns, adjusting indices
  const newColumnIdToIndex = new Map<string, number>()
  const newIndexToColumnId: string[] = []
  for (let i = 0; i < state.indexToColumnId.length; i++) {
    const colId = state.indexToColumnId[i]
    if (!removeSet.has(colId)) {
      const newIndex = newIndexToColumnId.length
      newColumnIdToIndex.set(colId, newIndex)
      newIndexToColumnId.push(colId)
    }
  }

  // Remove the columns' data from every row's rowData array or the column store, in one pass.
  const deletedIndices = columnIds
    .map((columnId) => state.columnIdToIndex.get(columnId))
    .filter((index): index is number => index !== undefined)
  let newRows = state.rows
  if (deletedIndices.length > 0) {
    if (state.columnStore) {
      // Splice highest index first so each removal doesn't shift the indices still to be removed.
      const descending = [...deletedIndices].sort((a, b) => b - a)
      descending.forEach((index) => state.columnStore!.deleteColumn(index))
    } else {
      const removeIndexSet = new Set(deletedIndices)
      newRows = state.rows.map((row) => {
        const newRowData = (row.rowData ?? []).filter((_, index) => !removeIndexSet.has(index))
        return { ...row, rowData: newRowData }
      })
    }
  }

  const newColumnWidths = calculateFrozenColumnWidths(newColumnsView, state.viewDetails.frozenIndex, newHiddenColumns)
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      hiddenColumns: newHiddenColumns,
      sortSettings: newSortSettings,
      filterSettings: newFilterSettings,
      colourSettings: newColourSettings,
      rowLockSettings: newRowLockSettings,
      groupSettings: newGroupSettings
    },
    tableDetails: { ...state.tableDetails, columns: newColumnsTable, joins: newJoins },
    tableView: newColumnsTableView,
    refreshing: false,
    columnWidths: newColumnWidths,
    columnValuesCount: newColumnValuesCount,
    viewDetails: {
      ...state.viewDetails,
      columns: newColumnsView,
      matrixSettings: cleanMatrixForRemovedColumns(state.viewDetails.matrixSettings)
    },
    rows: newRows,
    columnIdToIndex: newColumnIdToIndex,
    indexToColumnId: newIndexToColumnId
  }
}

// Delete one column — a thin wrapper over the set-based delete above.
const deleteTableColumn = (state: ISpreadsheetData, columnId: string) => deleteTableColumns(state, [columnId])

const changeColumnOrder = (state: ISpreadsheetData, columnId: string, newIndex: number) => {
  const newColumns = [...state.viewDetails.columns]
  const columnIndex = newColumns.findIndex((column: ITableViewColumn) => column.publicId === columnId)
  if (columnIndex !== -1) {
    arrayMove(newColumns, columnIndex, newIndex)
  }

  const newColumnWidths = calculateFrozenColumnWidths(
    newColumns,
    state.viewDetails.frozenIndex,
    state.userConfiguration.hiddenColumns
  )

  return {
    ...state,
    columnWidths: newColumnWidths,
    viewDetails: {
      ...state.viewDetails,
      columns: newColumns
    },
    userConfiguration: {
      ...state.userConfiguration,
      unsavedChanges: true
    }
  }
}

const changeAllColumnsOrder = (state: ISpreadsheetData, newColumns: ITableViewColumn[]) => {
  const newColumnWidths = calculateFrozenColumnWidths(
    newColumns,
    state.viewDetails.frozenIndex,
    state.userConfiguration.hiddenColumns
  )

  return {
    ...state,
    columnWidths: newColumnWidths,
    viewDetails: {
      ...state.viewDetails,
      columns: newColumns
    },
    userConfiguration: {
      ...state.userConfiguration,
      unsavedChanges: true
    }
  }
}

const changeRowOrder = (state: ISpreadsheetData, rowId: string, newIndex: number, newSortOrder: number) => {
  const newRows = [...state.rows]
  const rowIndex = newRows.findIndex((row: ITableRow) => row.publicId === rowId)
  if (rowIndex !== -1) {
    newRows[rowIndex].sortOrder = newSortOrder
    arrayMove(newRows, rowIndex, newIndex)
  }

  return {
    ...state,
    rows: newRows
  }
}

// Sort actions can also mutate groupSettings (a grouped column keeps its sort
// direction in sync), so the dirty flag must reflect BOTH dimensions diverging from
// the saved baseline. Diffing against viewDetails lets add-then-remove sort round-trip
// back to "no unsaved changes" without clearing an independent group change.
const sortConfigDiffersFromSaved = (
  sortSettings: ITableViewSort[],
  groupSettings: ITableViewGroup[],
  state: ISpreadsheetData
): boolean =>
  !areSortSettingsEqual(sortSettings, state.viewDetails.sortSettings) ||
  !areGroupSettingsEqual(groupSettings, state.viewDetails.groupSettings)

const addSort = (state: ISpreadsheetData, columnId: string, columnName: string) => {
  const newSortSettings = [
    ...state.userConfiguration.sortSettings,
    {
      columnId,
      columnName,
      direction: sortDirections.asc
    }
  ]
  const newGroupSettings = state.userConfiguration.groupSettings.map((group: ITableViewGroup) =>
    group.columnId === columnId ? { ...group, direction: sortDirections.asc } : group
  )
  const newUserConfiguration = {
    ...state.userConfiguration,
    unsavedChanges: sortConfigDiffersFromSaved(newSortSettings, newGroupSettings, state),
    sortSettings: newSortSettings,
    groupSettings: newGroupSettings
  }

  return {
    ...state,
    userConfiguration: newUserConfiguration
  }
}

const deleteSort = (state: ISpreadsheetData, columnId: string) => {
  const newUserConfiguration = { ...state.userConfiguration }
  newUserConfiguration.sortSettings = newUserConfiguration.sortSettings.filter(
    (sort: ITableViewSort) => sort.columnId !== columnId
  )
  newUserConfiguration.unsavedChanges = sortConfigDiffersFromSaved(
    newUserConfiguration.sortSettings,
    newUserConfiguration.groupSettings,
    state
  )
  return {
    ...state,
    userConfiguration: newUserConfiguration
  }
}

const changeSortDirection = (state: ISpreadsheetData, columnId: string, newDirection: sortDirections) => {
  const newSortSettings = state.userConfiguration.sortSettings.map((sort: ITableViewSort) =>
    sort.columnId === columnId ? { ...sort, direction: newDirection } : sort
  )
  const newGroupSettings = state.userConfiguration.groupSettings.map((group: ITableViewGroup) =>
    group.columnId === columnId ? { ...group, direction: newDirection } : group
  )
  const newUserConfiguration = {
    ...state.userConfiguration,
    sortSettings: newSortSettings,
    groupSettings: newGroupSettings,
    unsavedChanges: sortConfigDiffersFromSaved(newSortSettings, newGroupSettings, state)
  }

  return {
    ...state,
    userConfiguration: newUserConfiguration
  }
}

const changeSortOrder = (state: ISpreadsheetData, existingIndex: number, newIndex: number) => {
  const newSortSettings = [...state.userConfiguration.sortSettings]
  arrayMove(newSortSettings, existingIndex, newIndex)
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      sortSettings: newSortSettings,
      unsavedChanges: sortConfigDiffersFromSaved(newSortSettings, state.userConfiguration.groupSettings, state)
    }
  }
}

const showColumn = (state: ISpreadsheetData, columnId: string) => {
  let newHiddenColumns = [...state.userConfiguration.hiddenColumns]
  newHiddenColumns = newHiddenColumns.filter((columnPublicId) => columnPublicId !== columnId)

  // When showing a view as admin it is possible that the column is not part of the view anymore
  // This could happen after saving current view.
  const newViewColumns = [...state.viewDetails.columns]
  const foundInView = state.viewDetails.columns.find((col) => col.publicId === columnId)
  if (state.isAdmin && !foundInView) {
    const foundInTable = state.tableDetails.columns.find((col) => col.publicId === columnId)
    const sortOrder =
      state.viewDetails.columns.length > 0
        ? state.viewDetails.columns[state.viewDetails.columns.length - 1].sortOrder + 1
        : 0
    if (foundInTable) {
      // If you are keeping validations in sync you need to find
      // the column in another view
      let sourceColumn: ITableViewColumn | undefined
      for (let i = 0; i < state.tableView.length; i++) {
        const view = state.tableView[i]
        sourceColumn = view.columns.find((col) => col.publicId === columnId)
        break
      }

      newViewColumns.push({
        ...foundInTable,
        sortOrder: sortOrder,
        required: false,
        stringValidation:
          sourceColumn && state.tableDetails.keepValidationsInSync ? sourceColumn.stringValidation : null,
        validationMessage:
          sourceColumn && state.tableDetails.keepValidationsInSync ? sourceColumn.validationMessage : null,
        hardValidation: sourceColumn && state.tableDetails.keepValidationsInSync ? sourceColumn.hardValidation : false,
        validationNoBlanks:
          sourceColumn && state.tableDetails.keepValidationsInSync ? sourceColumn.validationNoBlanks : false,
        validationNoDuplicates:
          sourceColumn && state.tableDetails.keepValidationsInSync ? sourceColumn.validationNoDuplicates : false,
        displayValidationError:
          sourceColumn && state.tableDetails.keepValidationsInSync ? sourceColumn.displayValidationError : false,
        blockInvalidSelectValues:
          sourceColumn && state.tableDetails.keepValidationsInSync ? sourceColumn.blockInvalidSelectValues : false,
        formWidth: null
      })
    }
  }

  const newColumnWidths = calculateFrozenColumnWidths(newViewColumns, state.viewDetails.frozenIndex, newHiddenColumns)

  return {
    ...state,
    columnWidths: newColumnWidths,
    viewDetails: {
      ...state.viewDetails,
      columns: newViewColumns.map((column: ITableViewColumn, index: number) => {
        return { ...column, sortOrder: index }
      })
    },
    userConfiguration: {
      ...state.userConfiguration,
      hiddenColumns: newHiddenColumns,
      unsavedChanges: !areHiddenColumnsEqual(newHiddenColumns, state.viewDetails.hiddenColumns)
    }
  }
}

const hideColumn = (state: ISpreadsheetData, columnId: string) => {
  // Grouping / tree-group only render on spreadsheet views. Other view types (e.g. forms) can
  // inherit `groupSettings` / `treeGroupView` from the source view but never render them, so the
  // group/tree-leaf hide-protection must not apply there or those columns can never be hidden
  // (issue #10264).
  if (viewSupportsGrouping(state.viewDetails.type)) {
    // Prevent hiding grouped columns
    const isGrouped = state.userConfiguration.groupSettings.some((g) => g.columnId === columnId)
    if (isGrouped) return state

    // Prevent hiding the tree leaf column (first non-grouped column in tree group view)
    if (state.viewDetails.treeGroupView && state.userConfiguration.groupSettings.length > 0) {
      const groupedIds = state.userConfiguration.groupSettings.map((g) => g.columnId)
      const firstNonGrouped = state.viewDetails.columns.find((col) => !groupedIds.includes(col.publicId))
      if (firstNonGrouped && firstNonGrouped.publicId === columnId) return state
    }
  }

  // Prevent hiding the pivot column in transpose view
  if (state.viewDetails.transposeSettings?.pivotColumnId === columnId) return state

  // Skip if already hidden (prevents duplicates from bulk operations)
  if (state.userConfiguration.hiddenColumns.includes(columnId)) return state

  const newHiddenColumns = [...state.userConfiguration.hiddenColumns, columnId]
  const newColumnWidths = calculateFrozenColumnWidths(
    state.viewDetails.columns,
    state.viewDetails.frozenIndex,
    newHiddenColumns
  )

  return {
    ...state,
    columnWidths: newColumnWidths,
    userConfiguration: {
      ...state.userConfiguration,
      hiddenColumns: newHiddenColumns,
      unsavedChanges: !areHiddenColumnsEqual(newHiddenColumns, state.viewDetails.hiddenColumns)
    }
  }
}

const showAllColumns = (state: ISpreadsheetData) => {
  const newHiddenColumns: string[] = []
  const newViewColumns = getColumnsPerUserRole(state).map((col, index) => {
    return {
      ...col,
      sortOrder: index,
      required: false,
      stringValidation: null,
      validationMessage: null,
      hardValidation: false,
      validationNoBlanks: false,
      validationNoDuplicates: false,
      displayValidationError: true,
      blockInvalidSelectValues: true,
      formWidth: null
    }
  })
  const newColumnWidths = calculateFrozenColumnWidths(newViewColumns, state.viewDetails.frozenIndex, newHiddenColumns)
  return {
    ...state,
    columnWidths: newColumnWidths,
    viewDetails: { ...state.viewDetails, columns: newViewColumns },
    userConfiguration: {
      ...state.userConfiguration,
      hiddenColumns: newHiddenColumns,
      unsavedChanges: !areHiddenColumnsEqual(newHiddenColumns, state.viewDetails.hiddenColumns)
    }
  }
}

const hideAllColumns = (state: ISpreadsheetData) => {
  // Grouping only renders on spreadsheet views; other view types (e.g. forms) inherit
  // `groupSettings` from the source view but never render them, so grouped columns must still be
  // hideable there (issue #10264) — mirrors the gate in `hideColumn`.
  const supportsGrouping = viewSupportsGrouping(state.viewDetails.type)

  //  Filter out columns that are part of a group or the pivot column
  const newHiddenColumns = (state.isAdmin ? state.tableDetails.columns : state.viewDetails.columns)
    .filter(
      (column) =>
        !(
          supportsGrouping && state.userConfiguration.groupSettings.find((group) => group.columnId === column.publicId)
        ) && column.publicId !== state.viewDetails.transposeSettings?.pivotColumnId
    )
    .map((column) => column.publicId)

  const newColumnWidths = calculateFrozenColumnWidths(
    state.viewDetails.columns,
    state.viewDetails.frozenIndex,
    newHiddenColumns
  )

  return {
    ...state,
    columnWidths: newColumnWidths,
    userConfiguration: {
      ...state.userConfiguration,
      hiddenColumns: newHiddenColumns,
      unsavedChanges: !areHiddenColumnsEqual(newHiddenColumns, state.viewDetails.hiddenColumns)
    }
  }
}

const requireColumn = (state: ISpreadsheetData, columnId: string) => {
  const newViewDetails = {
    ...state.viewDetails,
    columns: state.viewDetails.columns.map((column: ITableViewColumn) => {
      if (column.publicId === columnId) {
        const newColumn = Object.assign({}, column)
        newColumn.required = true
        return newColumn
      } else {
        return column
      }
    })
  }
  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const unrequireColumn = (state: ISpreadsheetData, columnId: string) => {
  const newViewDetails = {
    ...state.viewDetails,
    columns: state.viewDetails.columns.map((column: ITableViewColumn) => {
      if (column.publicId === columnId) {
        const newColumn = Object.assign({}, column)
        newColumn.required = false
        return newColumn
      } else {
        return column
      }
    })
  }
  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const unrequireAllColumns = (state: ISpreadsheetData) => {
  const newViewDetails = {
    ...state.viewDetails,
    columns: state.viewDetails.columns.map((column: ITableViewColumn) => {
      const newColumn = Object.assign({}, column)
      newColumn.required = false
      return newColumn
    })
  }
  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const requireAllColumns = (state: ISpreadsheetData) => {
  const rowLockSettings = state.userConfiguration.rowLockSettings
  const newViewDetails = {
    ...state.viewDetails,
    columns: state.viewDetails.columns.map((column: ITableViewColumn) => {
      const newColumn = Object.assign({}, column)
      // Don't mark as required if column is joined or locked (locked + required is invalid).
      // Lock state lives in row-lock rules now, not the retired column.locked boolean.
      if (!column.isJoined && !isColumnLockedByRules(rowLockSettings, column.publicId)) {
        newColumn.required = true
      }
      return newColumn
    })
  }
  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeDisplayValidationRows = (state: ISpreadsheetData, displayValidationErrorRows: number) => {
  const newViewDetails = { ...state.viewDetails, displayValidationErrorRows }

  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeDisplayCommentRows = (state: ISpreadsheetData, displayCommentRows: number) => {
  const newViewDetails = { ...state.viewDetails, displayCommentRows }

  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeCollapsedGroupView = (state: ISpreadsheetData, collapsedGroupView: boolean) => {
  const newViewDetails = { ...state.viewDetails, collapsedGroupView }

  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeTreeGroupView = (state: ISpreadsheetData, treeGroupView: boolean) => {
  const hasGroupedColumns = state.userConfiguration.groupSettings.length > 0
  const frozenIndex =
    treeGroupView && hasGroupedColumns && state.viewDetails.frozenIndex < 1 ? 1 : state.viewDetails.frozenIndex
  const newViewDetails = { ...state.viewDetails, treeGroupView, frozenIndex }
  const newColumnWidths =
    frozenIndex !== state.viewDetails.frozenIndex
      ? calculateFrozenColumnWidths(state.viewDetails.columns, frozenIndex, state.userConfiguration.hiddenColumns)
      : state.columnWidths

  return {
    ...state,
    viewDetails: newViewDetails,
    columnWidths: newColumnWidths,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeUnpackMultiselectGroupView = (state: ISpreadsheetData, unpackMultiselectGroupView: boolean) => {
  const newViewDetails = { ...state.viewDetails, unpackMultiselectGroupView }

  return {
    ...state,
    viewDetails: newViewDetails,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const changeFrozenIndex = (state: ISpreadsheetData, frozenIndex: number) => {
  const newViewDetails = { ...state.viewDetails, frozenIndex: frozenIndex }
  const newColumnWidths = calculateFrozenColumnWidths(
    state.viewDetails.columns,
    frozenIndex,
    state.userConfiguration.hiddenColumns
  )

  return {
    ...state,
    viewDetails: newViewDetails,
    columnWidths: newColumnWidths,
    userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
  }
}

const updateTable = (
  state: ISpreadsheetData,
  field:
    | 'name'
    | 'logo'
    | 'keepValidationsInSync'
    | 'keepColoursInSync'
    | 'isDeleted'
    | 'deletedAt'
    | 'syncHourlyFrequency'
    | 'isSyncing'
    | 'failedSyncAttempts'
    | 'lastSync'
    | 'allowComments',
  value: string | string[] | boolean | number | null
) => {
  return {
    ...state,
    tableDetails: {
      ...state.tableDetails,
      name: field === 'name' && typeof value === 'string' ? value : state.tableDetails.name,
      keepValidationsInSync:
        field === 'keepValidationsInSync' && typeof value === 'boolean'
          ? value
          : state.tableDetails.keepValidationsInSync,
      keepColoursInSync:
        field === 'keepColoursInSync' && typeof value === 'boolean' ? value : state.tableDetails.keepColoursInSync,
      isDeleted: field === 'isDeleted' && typeof value === 'boolean' ? value : state.tableDetails.isDeleted,
      deletedAt:
        field === 'deletedAt' && (typeof value === 'string' || value === null) ? value : state.tableDetails.deletedAt,
      syncHourlyFrequency:
        field === 'syncHourlyFrequency' && typeof value === 'number' ? value : state.tableDetails.syncHourlyFrequency,
      isSyncing: field === 'isSyncing' && typeof value === 'boolean' ? value : state.tableDetails.isSyncing,
      failedSyncAttempts:
        field === 'failedSyncAttempts' && typeof value === 'number' ? value : state.tableDetails.failedSyncAttempts,
      lastSync: field === 'lastSync' && typeof value === 'string' ? value : state.tableDetails.lastSync,
      allowComments: field === 'allowComments' && typeof value === 'boolean' ? value : state.tableDetails.allowComments
    }
  }
}

const updateTableJoins = (state: ISpreadsheetData, table: ITable) => {
  let newState: ISpreadsheetData = {
    ...state,
    tableDetails: {
      ...state.tableDetails,
      columns: state.tableDetails.columns.map((col) => {
        return { ...col }
      })
    },
    viewDetails: {
      ...state.viewDetails,
      columns: state.viewDetails.columns.map((col) => {
        return { ...col }
      })
    }
  }
  const hiddenColumns = state.isAdmin
    ? state.userConfiguration.hiddenColumns
    : state.viewDetails.hiddenColumns.map((hiddenColumn) => hiddenColumn.columnId)

  // Filter out all joined columns that have been deleted
  const joinedColumns = table.columns.filter((col) => col.isJoined)
  const newHiddenColumns = hiddenColumns.filter(
    (hiddenColumn) => !!table.columns.find((column) => column.publicId === hiddenColumn)
  )
  const newJoinedColumns = joinedColumns.filter((col) => !hiddenColumns.includes(col.publicId))
  // Remove all joined columns from table
  state.viewDetails.columns
    .filter((col) => col.isJoined)
    .filter((viewColumn) => !newJoinedColumns.find((col) => viewColumn.publicId === col.publicId))
    .forEach((col) => {
      newState = deleteTableColumn(newState, col.publicId)
    })

  // Add all joined columns from updated table into the view
  const maxSortOrder = Math.max(...newState.viewDetails.columns.map((col: ITableViewColumn) => col.sortOrder))
  newJoinedColumns
    .filter((col) => !newState.viewDetails.columns.find((viewColumn) => viewColumn.publicId === col.publicId))
    .forEach((col, index) => {
      const newCol = {
        sortOrder: index + maxSortOrder + 1,
        required: false, // Joined columns are implicitly locked, so cannot be required
        ...col,
        stringValidation: null,
        validationMessage: null,
        validationNoBlanks: false,
        validationNoDuplicates: false,
        hardValidation: false,
        displayValidationError: true,
        blockInvalidSelectValues: true,
        formWidth: null
      }
      newState = createTableColumn(newState, newCol)
      newState = showColumn(newState, newCol.publicId)
    })
  let updatedState = {
    ...newState,
    tableDetails: { ...state.tableDetails, joins: table.joins, columns: table.columns },
    userConfiguration: { ...state.userConfiguration, hiddenColumns: newHiddenColumns }
  }
  updatedState = saveCurrentView(updatedState)
  return updatedState
}

const addView = (state: ISpreadsheetData, view: ITableViewWithColumns, inProcess: boolean) => {
  const viewColumnIds = view.columns.map((column: ITableViewColumn) => column.publicId)
  const newTableView = [...state.tableView]

  let hiddenColumns: { columnId: string; columnName: string }[]
  let missingColumns: ITableViewColumn[]

  if (inProcess && view.isPersonalView) {
    // Personal view path: derive hidden columns from the configuration parent view.
    // The new view only contains the parent's *visible* columns (actions.addNewView
    // filters out hidden ones), so anything in the parent but not in the new view
    // is what was hidden.
    const parentView = view.configurationParentView
      ? state.tableView.find((v) => v.publicId === view.configurationParentView?.publicId)
      : null

    if (parentView) {
      const result = calculateHiddenColumnsFromParent(view.columns, parentView.columns)
      hiddenColumns = result.hiddenColumnsArray
      missingColumns = result.missingColumns
    } else {
      hiddenColumns = []
      missingColumns = []
    }
  } else if (state.tableDetails.columns && state.tableDetails.columns.length > 0) {
    // Admin path: derive hidden columns from table columns
    hiddenColumns = state.tableDetails.columns
      .filter((column: ITableColumn) => !viewColumnIds.includes(column.publicId))
      .map((column: ITableColumn) => ({ columnId: column.publicId, columnName: column.name }))
    missingColumns = getMissingColumns(
      hiddenColumns.map((col) => col.columnId),
      { ...state, tableView: newTableView }
    )
  } else {
    hiddenColumns = []
    missingColumns = []
  }

  const enrichedView = {
    ...view,
    hiddenColumns,
    columns: view.columns.concat(missingColumns),
    columnsHydrated: true
  }

  newTableView.push(enrichedView)
  return setView({ ...state, tableView: newTableView }, view.publicId, inProcess)
}

const mergeFetchedView = (state: ISpreadsheetData, fetched: ITableViewWithColumns): ISpreadsheetData => {
  const viewColumnIds = fetched.columns.map((c: ITableViewColumn) => c.publicId)

  let hiddenColumns: { columnId: string; columnName: string }[] = []
  let missingColumns: ITableViewColumn[] = []

  if (state.tableDetails.columns && state.tableDetails.columns.length > 0) {
    // Admin path: hidden columns = table columns not in this view
    hiddenColumns = state.tableDetails.columns
      .filter((column: ITableColumn) => !viewColumnIds.includes(column.publicId))
      .map((column: ITableColumn) => ({ columnId: column.publicId, columnName: column.name }))
    missingColumns = getMissingColumns(
      hiddenColumns.map((col) => col.columnId),
      { ...state, tableView: state.tableView }
    )
  } else {
    // Personal view path: derive from configurationParentView if available in state.tableView
    const parentView = fetched.configurationParentView
      ? state.tableView.find((v) => v.publicId === fetched.configurationParentView?.publicId)
      : null
    if (parentView && parentView.columns.length > 0) {
      // Parent columns must be available to derive hidden columns. Task 3 hydrates the parent before dispatch, so the missing-parent case is a no-op.
      const result = calculateHiddenColumnsFromParent(fetched.columns, parentView.columns)
      hiddenColumns = result.hiddenColumnsArray
      missingColumns = result.missingColumns
    }
  }

  const enriched: ITableViewWithColumns = {
    ...fetched,
    filterSettings: fetched.filterSettings.map(mapUnknownFilterTypes),
    colourSettings: fetched.colourSettings.map(mapUnknownFilterTypes),
    hiddenColumns,
    columns: fetched.columns.concat(missingColumns),
    columnsHydrated: true
  }

  const newTableView = state.tableView.map((v) => (v.publicId === enriched.publicId ? enriched : v))
  return { ...state, tableView: newTableView }
}

const setView = (
  state: ISpreadsheetData,
  viewId: string,
  inProcess?: boolean,
  fetchedViewDetails?: ITableViewWithColumns
) => {
  let workingState = state
  if (fetchedViewDetails) {
    const existing = workingState.tableView.find((v) => v.publicId === viewId)
    const alreadyHydrated = !!existing?.columnsHydrated
    if (!alreadyHydrated) {
      workingState = mergeFetchedView(state, fetchedViewDetails)
    }
  }
  const targetView = workingState.tableView.find((view: ITableViewWithColumns) => view.publicId === viewId)
  const newState = { ...workingState }

  if (targetView) {
    newState['userConfiguration']['unsavedChanges'] = false
    newState['activeTableView'] = viewId
    newState['viewDetails'] = { ...targetView }
    newState['originalViewColumns'] = targetView.columns.map((column: ITableViewColumn) => column.name)

    // If we are inside a document and it is a personal view
    // Then we want to remove all the parent view filter settings
    // so that they are not saved over
    if (inProcess && targetView.isPersonalView) {
      // First find the parent view
      const parentView = state.tableView.find(
        (view: ITableViewWithColumns) =>
          targetView.configurationParentView && view.publicId === targetView.configurationParentView.publicId
      )
      if (parentView) {
        // Now filter out all the filter settings with the same public ID as the parent
        newState['viewDetails']['filterSettings'] = targetView.filterSettings.filter((setting) => {
          return !parentView.filterSettings.find(
            (parentSetting: ITableViewFilter) => parentSetting.publicId === setting.publicId
          )
        })

        // Calculate hidden columns from the configuration parent view
        const { hiddenColumnIds, missingColumns, hiddenColumnsArray } = calculateHiddenColumnsFromParent(
          targetView.columns,
          parentView.columns
        )

        // Also include any hidden columns already stored in the target view
        const existingHiddenColumnIds = Array.isArray(targetView.hiddenColumns)
          ? targetView.hiddenColumns.map((col) => col.columnId)
          : []

        // Merge hidden column IDs (deduplicated)
        const allHiddenColumnIds = [...new Set([...hiddenColumnIds, ...existingHiddenColumnIds])]

        // Merge hidden columns arrays (deduplicated by columnId)
        const existingHiddenColumnsArray = Array.isArray(targetView.hiddenColumns) ? targetView.hiddenColumns : []
        const allHiddenColumnsArray = [
          ...hiddenColumnsArray,
          ...existingHiddenColumnsArray.filter(
            (existing) => !hiddenColumnsArray.some((calc) => calc.columnId === existing.columnId)
          )
        ]

        newState['userConfiguration']['hiddenColumns'] = allHiddenColumnIds
        newState['viewDetails']['columns'] = [...targetView.columns, ...missingColumns]
        newState['viewDetails']['hiddenColumns'] = allHiddenColumnsArray
      }
    } else if (inProcess && !targetView.isPersonalView) {
      newState['viewDetails']['filterSettings'] = []
    }

    newState['userConfiguration']['filterSettings'] = newState['viewDetails']['filterSettings']
    newState['userConfiguration']['sortSettings'] = newState['viewDetails']['sortSettings']
    newState['userConfiguration']['groupSettings'] = newState['viewDetails']['groupSettings']
    newState['userConfiguration']['colourSettings'] = newState['viewDetails']['colourSettings']
    newState['userConfiguration']['rowLockSettings'] = newState['viewDetails']['rowLockSettings'] ?? []
    newState['userConfiguration']['chartSettings'] = newState['viewDetails']['chartSettings']
    newState['userConfiguration']['calendarSettings'] = newState['viewDetails']['calendarSettings']

    // Only set hiddenColumns from viewDetails if not already calculated from parent view
    if (!inProcess || !targetView.isPersonalView) {
      newState['userConfiguration']['hiddenColumns'] = Array.isArray(newState['viewDetails']['hiddenColumns'])
        ? newState['viewDetails']['hiddenColumns'].map((hiddenColumn) => hiddenColumn.columnId)
        : []
    }

    const columnWidths = calculateFrozenColumnWidths(
      newState['viewDetails']['columns'],
      targetView['frozenIndex'],
      newState['userConfiguration']['hiddenColumns']
    )
    newState['columnWidths'] = columnWidths
  } else {
    newState['activeTableView'] = viewId
  }

  return newState
}

const saveCurrentView = (state: ISpreadsheetData) => {
  const newState = { ...state, userConfiguration: { ...state.userConfiguration, unsavedChanges: false } }
  const newView = state['viewDetails']
  const configurationParentView = state.tableView.find((view) =>
    state['viewDetails'].configurationParentView
      ? view.publicId === state['viewDetails'].configurationParentView.publicId
      : null
  )

  if (!state.isAdmin) {
    newView['columns'] = state['viewDetails'].columns.filter(
      (column: ITableViewColumn) => !newState.userConfiguration.hiddenColumns.includes(column.publicId)
    )
  }

  const missingColumns = newState['isAdmin']
    ? getMissingColumns(newState['userConfiguration']['hiddenColumns'], newState)
    : configurationParentView
      ? getMissingColumnsFromParentView(newState['userConfiguration']['hiddenColumns'], configurationParentView.columns)
      : []

  newView['hiddenColumns'] = missingColumns.map((col) => {
    return { columnId: col.publicId, columnName: col.name }
  })

  newState['tableView'] = state.tableView.map((view: ITableViewWithColumns) =>
    view.publicId === state.viewDetails.publicId ? newView : view
  )
  newView['filterSettings'] = convertToApiFilter(newState.userConfiguration.filterSettings)
  newView['groupSettings'] = newState.userConfiguration.groupSettings
  newView['sortSettings'] = newState.userConfiguration.sortSettings
  newView['colourSettings'] = convertToApiColour(newState.userConfiguration.colourSettings)
  newView['rowLockSettings'] = convertToApiRowLock(newState.userConfiguration.rowLockSettings)
  newView['chartSettings'] = newState.userConfiguration.chartSettings
  newView['calendarSettings'] = newState.userConfiguration.calendarSettings

  // Business logic: we do not sync colour settings from personal views
  if (state.tableDetails.keepColoursInSync && !state.viewDetails.isPersonalView) {
    // Business logic: we do not sync colour settings to personal views
    newState['tableView'] = newState['tableView'].map((view) => {
      return {
        ...view,
        colourSettings: view.isPersonalView
          ? view.colourSettings
          : convertToApiColour(newState.userConfiguration.colourSettings)
      }
    })
  }

  return newState
}

const updateView = (
  state: ISpreadsheetData,
  field:
    | 'name'
    | 'description'
    | 'type'
    | 'disableNewRow'
    | 'disableSyncCsv'
    | 'allowContributorDelete'
    | 'displayValidationErrorRows'
    | 'displayCommentRows'
    | 'collapsedGroupView'
    | 'unpackMultiselectGroupView'
    | 'treeGroupView',
  viewId: string,
  value: string | number | EditorContent | null | boolean
) => {
  const newTableView = state.tableView.map((tableView) =>
    tableView.publicId === viewId
      ? {
          ...tableView,
          name: field === 'name' && typeof value === 'string' ? value : tableView.name,
          description:
            field === 'description' &&
            typeof value !== 'string' &&
            typeof value !== 'number' &&
            typeof value !== 'boolean'
              ? value
              : tableView.description,
          type: field === 'type' && typeof value === 'number' ? value : tableView.type,
          disableNewRow: field === 'disableNewRow' && typeof value === 'boolean' ? value : tableView.disableNewRow,
          disableSyncCsv: field === 'disableSyncCsv' && typeof value === 'boolean' ? value : tableView.disableSyncCsv,
          allowContributorDelete:
            field === 'allowContributorDelete' && typeof value === 'boolean' ? value : tableView.allowContributorDelete,
          displayValidationErrorRows:
            field === 'displayValidationErrorRows' && typeof value === 'number'
              ? value
              : tableView.displayValidationErrorRows,
          displayCommentRows:
            field === 'displayCommentRows' && typeof value === 'number' ? value : tableView.displayCommentRows,
          collapsedGroupView:
            field === 'collapsedGroupView' && typeof value === 'boolean' ? value : tableView.collapsedGroupView,
          unpackMultiselectGroupView:
            field === 'unpackMultiselectGroupView' && typeof value === 'boolean'
              ? value
              : tableView.unpackMultiselectGroupView,
          treeGroupView: field === 'treeGroupView' && typeof value === 'boolean' ? value : tableView.treeGroupView
        }
      : tableView
  )

  const newViewDetails = { ...state.viewDetails }
  if (state.viewDetails.publicId === viewId) {
    if (field === 'name' && typeof value === 'string') newViewDetails.name = value
    else if (field === 'type' && typeof value === 'number') newViewDetails.type = value
    else if (field === 'disableNewRow' && typeof value === 'boolean') newViewDetails.disableNewRow = value
    else if (field === 'disableSyncCsv' && typeof value === 'boolean') newViewDetails.disableSyncCsv = value
    else if (field === 'allowContributorDelete' && typeof value === 'boolean')
      newViewDetails.allowContributorDelete = value
    else if (field === 'displayValidationErrorRows' && typeof value === 'number')
      newViewDetails.displayValidationErrorRows = value
    else if (field === 'displayCommentRows' && typeof value === 'number') newViewDetails.displayCommentRows = value
    else if (field === 'collapsedGroupView' && typeof value === 'boolean') newViewDetails.collapsedGroupView = value
    else if (field === 'unpackMultiselectGroupView' && typeof value === 'boolean') {
      newViewDetails.unpackMultiselectGroupView = value
    } else if (field === 'treeGroupView' && typeof value === 'boolean') {
      newViewDetails.treeGroupView = value
    } else if (
      field === 'description' &&
      typeof value !== 'string' &&
      typeof value !== 'number' &&
      typeof value !== 'boolean'
    )
      newViewDetails.description = value
  }

  return {
    ...state,
    viewDetails: newViewDetails,
    tableView: newTableView
  }
}

const deleteView = (state: ISpreadsheetData, viewId: string) => {
  const newTableView = state.tableView.filter((tableView) => tableView.publicId !== viewId)
  return {
    ...state,
    tableView: newTableView
  }
}

const addGroup = (state: ISpreadsheetData, columnId: string, columnName: string) => {
  const group = {
    columnId,
    columnName,
    direction: sortDirections.asc
  }
  const isFirstGroup = state.userConfiguration.groupSettings.length === 0
  const needsFrozenBump = state.viewDetails.treeGroupView && isFirstGroup && state.viewDetails.frozenIndex < 1

  const frozenIndex = needsFrozenBump ? 1 : state.viewDetails.frozenIndex
  const newViewDetails = needsFrozenBump ? { ...state.viewDetails, frozenIndex } : state.viewDetails
  const newColumnWidths = needsFrozenBump
    ? calculateFrozenColumnWidths(state.viewDetails.columns, frozenIndex, state.userConfiguration.hiddenColumns)
    : state.columnWidths

  const newGroupSettings = [...state.userConfiguration.groupSettings, group]

  return {
    ...state,
    viewDetails: newViewDetails,
    columnWidths: newColumnWidths,
    userConfiguration: {
      ...state.userConfiguration,
      groupSettings: newGroupSettings,
      // A frozen-index bump mutates the view config (frozenIndex is not baseline-tracked;
      // changeFrozenIndex/changeTreeGroupView treat any such change as dirty), so it must
      // mark the view dirty even when the grouping itself is net-zero against the baseline.
      unsavedChanges: needsFrozenBump || !areGroupSettingsEqual(newGroupSettings, state.viewDetails.groupSettings)
    }
  }
}

const addChart = (state: ISpreadsheetData, chartSettings: ITableViewChart) => {
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      chartSettings: chartSettings,
      unsavedChanges: true
    }
  }
}

const deleteGroup = (state: ISpreadsheetData, columnId: string) => {
  const newUserConfiguration = { ...state.userConfiguration }
  newUserConfiguration.groupSettings = newUserConfiguration.groupSettings.filter((sort) => sort.columnId !== columnId)
  newUserConfiguration.unsavedChanges = !areGroupSettingsEqual(
    newUserConfiguration.groupSettings,
    state.viewDetails.groupSettings
  )

  // When the last group is removed and tree view is on, the tree column no longer renders
  // so reset frozenIndex back to the saved view value and clear treeGroupView
  // (undoing the automatic bump from addGroup/changeTreeGroupView)
  const isLastGroupRemoved = newUserConfiguration.groupSettings.length === 0 && state.viewDetails.treeGroupView
  if (isLastGroupRemoved) {
    const savedView = state.tableView.find((v) => v.publicId === state.activeTableView)
    const savedFrozenIndex = savedView?.frozenIndex ?? 0
    const newViewDetails = { ...state.viewDetails, frozenIndex: savedFrozenIndex, treeGroupView: false }
    const newColumnWidths = calculateFrozenColumnWidths(
      state.viewDetails.columns,
      savedFrozenIndex,
      newUserConfiguration.hiddenColumns
    )
    return {
      ...state,
      viewDetails: newViewDetails,
      columnWidths: newColumnWidths,
      userConfiguration: newUserConfiguration
    }
  }

  return {
    ...state,
    userConfiguration: newUserConfiguration
  }
}

const changeGroupDirection = (state: ISpreadsheetData, columnId: string, newDirection: sortDirections) => {
  const newGroupSettings = state.userConfiguration.groupSettings.map((group) =>
    group.columnId === columnId ? { ...group, direction: newDirection } : group
  )
  const newUserConfiguration = {
    ...state.userConfiguration,
    unsavedChanges: !areGroupSettingsEqual(newGroupSettings, state.viewDetails.groupSettings),
    groupSettings: newGroupSettings
  }

  return {
    ...state,
    userConfiguration: newUserConfiguration
  }
}

// Swaps the column a group is by, in place: same position and direction. A delete then an add
// would pass through having no groups, which turns a tree view off.
const changeGroupColumn = (state: ISpreadsheetData, index: number, columnId: string, columnName: string) => {
  const newGroupSettings = state.userConfiguration.groupSettings.map((group, at) =>
    at === index ? { ...group, columnId, columnName } : group
  )
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      groupSettings: newGroupSettings,
      unsavedChanges: !areGroupSettingsEqual(newGroupSettings, state.viewDetails.groupSettings)
    }
  }
}

const changeGroupOrder = (state: ISpreadsheetData, existingIndex: number, newIndex: number) => {
  const newGroupSettings = [...state.userConfiguration.groupSettings]
  arrayMove(newGroupSettings, existingIndex, newIndex)
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      groupSettings: newGroupSettings,
      unsavedChanges: !areGroupSettingsEqual(newGroupSettings, state.viewDetails.groupSettings)
    }
  }
}

const toggleGroupCollapse = (state: ISpreadsheetData, groupId: string) => {
  const newSet = new Set(state.collapsedGroupIds)
  if (newSet.has(groupId)) {
    newSet.delete(groupId)
  } else {
    newSet.add(groupId)
  }

  return { ...state, collapsedGroupIds: newSet }
}

const expandAllGroups = (state: ISpreadsheetData) => {
  const newSet = new Set<string>()
  return { ...state, collapsedGroupIds: newSet, collapsedColumnIds: new Set<string>() }
}

const collapseAllGroups = (state: ISpreadsheetData, groupIds: string[]) => {
  // Validate that all grouped columns exist in the view
  const missingColumns = state.userConfiguration.groupSettings.filter(
    (groupSetting) => !state.viewDetails.columns.some((column) => column.publicId === groupSetting.columnId)
  )

  if (missingColumns.length > 0) {
    return state
  }

  const newSet = new Set(state.collapsedGroupIds)

  for (let i = 0; i < groupIds.length; i++) {
    newSet.add(groupIds[i])
  }

  const toGroupColumnsIds = state.userConfiguration.groupSettings
    .map((toGroupColumn) => {
      const column = state.viewDetails.columns.find((c) => c.publicId === toGroupColumn.columnId)
      return column?.publicId
    })
    .filter((id): id is string => id != null)

  return { ...state, collapsedGroupIds: newSet, collapsedColumnIds: new Set<string>(toGroupColumnsIds) }
}

const editCell = (
  state: ISpreadsheetData,
  columnId: string,
  rowId: string,
  value: { [key: string]: ICellValue },
  oldValue?: ICellValue
) => {
  const row = state.rows.find((row: ITableRow) => row.publicId === rowId)
  const newColumnValuesCount = { ...state.columnValuesCount }

  // Only update columnValuesCount if this column is being tracked (has duplicate validation)
  const isTrackedColumn = columnId in state.columnValuesCount
  const foldCase = isTrackedColumn && caseInsensitiveDuplicateColumns(state).has(columnId)

  if (row && isTrackedColumn) {
    if (oldValue) {
      const oldValueString = getDuplicateValueKey(oldValue.toString(), foldCase)

      if (newColumnValuesCount[columnId][oldValueString] !== undefined) {
        newColumnValuesCount[columnId][oldValueString] -= 1
      } else {
        newColumnValuesCount[columnId][oldValueString] = 0
      }
    }
  }

  const newRows: ITableRow[] = state.rows.map((row: ITableRow) => {
    if (row.publicId === rowId) {
      const columnIndex = state.columnIdToIndex.get(columnId)
      if (columnIndex !== undefined) {
        if (state.columnStore && row._rowIndex >= 0) {
          state.columnStore.set(row._rowIndex, columnIndex, value[columnId])
          return row
        }
        const newRowData = [...(row.rowData ?? [])]
        newRowData[columnIndex] = value[columnId]
        return { ...row, rowData: newRowData }
      }
    }
    return row // Return reference, don't clone unmodified rows
  })

  const newValue = value[columnId]

  if (newValue && isTrackedColumn) {
    const newValueString = getDuplicateValueKey(newValue.toString(), foldCase)
    if (newColumnValuesCount[columnId][newValueString] !== undefined)
      newColumnValuesCount[columnId][newValueString] += 1
    else {
      newColumnValuesCount[columnId][newValueString] = 1
    }
  }

  return {
    ...state,
    columnValuesCount: newColumnValuesCount,
    rows: newRows,
    refreshing: false,
    dataVersion: state.dataVersion + 1
  }
}

// Update the current filters, the actual filtering is done using an effect
// A change made in one view that lands once another is showing (a panel's delayed save, after the
// author switched view) belongs to the view it was made in, not this one.
const isForAnotherView = (state: ISpreadsheetData, viewId: string | undefined): boolean =>
  viewId !== undefined && viewId !== state.viewDetails.publicId

const updateFilters = (state: ISpreadsheetData, filters: ITableViewFilter[]) => {
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      filterSettings: filters,
      unsavedChanges: !areFilterSettingsEqual(filters, state.viewDetails.filterSettings)
    }
  }
}

// Update the current colours
const updateColours = (state: ISpreadsheetData, colours: ITableViewColour[]) => {
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      colourSettings: colours,
      unsavedChanges: !areColourSettingsEqual(colours, state.viewDetails.colourSettings)
    }
  }
}

const updateLocks = (state: ISpreadsheetData, locks: ITableViewRowLock[]) => {
  return {
    ...state,
    userConfiguration: {
      ...state.userConfiguration,
      rowLockSettings: locks,
      // viewDetails.rowLockSettings can be undefined at runtime for views the
      // backend serialised without the field (setView defaults it with ?? []
      // for the same reason), and recordListsEqual reads .length — so guard here.
      unsavedChanges: !areRowLockSettingsEqual(locks, state.viewDetails.rowLockSettings ?? [])
    }
  }
}

const setRows = (
  state: ISpreadsheetData,
  rows: ITableRow[],
  totalRows: number,
  visibleBitmap?: Uint8Array | null,
  precomputedVisibleCount?: number
) => {
  const bitmap = visibleBitmap !== undefined ? visibleBitmap : state.visibleBitmap
  const visibleCount =
    precomputedVisibleCount !== undefined ? precomputedVisibleCount : bitmap ? countBitmapOnes(bitmap) : rows.length
  return { ...state, rows, totalRows, visibleBitmap: bitmap, visibleCount }
}

function countBitmapOnes(bitmap: Uint8Array): number {
  let count = 0
  for (let i = 0; i < bitmap.length; i++) {
    if (bitmap[i]) count++
  }
  return count
}

const appendSpreadsheetRows = (state: ISpreadsheetData, rows: ITableRow[]) => {
  return {
    ...state,
    rows: [...state.rows, ...rows],
    totalRows: state.totalRows + rows.length
  }
}

// Rows (and their duplicate-validation tallies) that arrived since the last dispatch of a
// progressive stream. The grid is interactive while the stream is still running, so the rows are
// appended to the current state and the tallies added to the current counts rather than either
// being replaced by the stream's own accumulator: a row the user (or a socket event) inserted,
// deleted or edited in the meantime would otherwise silently revert when the next batch landed.
// Like the edit reducers, this copies the outer counts map and adds to the per-column maps in place.
const appendStreamedRows = (
  state: ISpreadsheetData,
  rows: ITableRow[],
  columnValuesCount: IColumnValuesCount
): ISpreadsheetData => {
  const nextRows = rows.length > 0 ? [...state.rows, ...rows] : state.rows
  return applyPendingStreamChanges(
    {
      ...state,
      rows: nextRows,
      totalRows: nextRows.length,
      visibleBitmap: null,
      visibleCount: nextRows.length,
      columnValuesCount: mergeColumnValuesCount({ ...state.columnValuesCount }, columnValuesCount)
    },
    rows
  )
}

// A row deleted or updated before its batch arrived comes in as the server's cursor saw it, so
// the batch is appended first and the pending change then applied through the ordinary reducers,
// which keep the duplicate tallies, the column store and the counts right.
const applyPendingStreamChanges = (state: ISpreadsheetData, arrived: ITableRow[]): ISpreadsheetData => {
  const { streamPendingDeletes, streamPendingUpdates } = state
  if (!streamPendingDeletes?.size && !streamPendingUpdates?.size) return state

  let next = state
  if (streamPendingDeletes?.size) {
    const deletedIds = arrived.map((r) => r.publicId).filter((id) => streamPendingDeletes.has(id))
    if (deletedIds.length > 0) {
      const remaining = new Set(streamPendingDeletes)
      deletedIds.forEach((id) => remaining.delete(id))
      next = { ...deleteRows(next, deletedIds), streamPendingDeletes: remaining }
    }
  }
  if (streamPendingUpdates?.size) {
    const updates: ITableRow[] = []
    for (const r of arrived) {
      const update = streamPendingUpdates.get(r.publicId)
      if (update) updates.push(update)
    }
    if (updates.length > 0) {
      const remaining = new Map(streamPendingUpdates)
      updates.forEach((u) => remaining.delete(u.publicId))
      next = { ...updateRows(next, updates), streamPendingUpdates: remaining }
    }
  }
  return next
}

const refreshSpreadsheetRows = (state: ISpreadsheetData, rows: ITableRow[], totalRows: number) => {
  return {
    ...state,
    rows: rows,
    totalRows: totalRows,
    visibleBitmap: null,
    visibleCount: totalRows
  }
}

const updateTableColumn = (state: ISpreadsheetData, column: ITableViewColumn) => {
  // update view column
  const newColumns = [...state.viewDetails.columns]
  const columnNumber = newColumns.findIndex((newColumn) => newColumn.publicId === column.publicId)

  if (columnNumber === -1) {
    console.error('The column can not be found in the view. Please save the view, refresh the page and try again.')
    return state
  }
  const previousColumnKind = newColumns[columnNumber].kind
  const previousColumnName = newColumns[columnNumber].name
  const previousValidationNoDuplicates = newColumns[columnNumber].validationNoDuplicates
  const prevColumnNameFormula = `$${previousColumnName}$`
  const newColumnNameFormula = `$${column.name}$`
  newColumns[columnNumber] = column

  newColumns.forEach((viewColumn: ITableViewColumn) => {
    if (viewColumn.formula) {
      viewColumn.formula = viewColumn.formula.replaceAll(prevColumnNameFormula, newColumnNameFormula)
    }
  })

  let filters = [...state.userConfiguration.filterSettings]
  let colours = [...state.userConfiguration.colourSettings]
  let rowLocks = [...state.userConfiguration.rowLockSettings]
  let groups = [...state.userConfiguration.groupSettings]
  let sorts = [...state.userConfiguration.sortSettings]
  const updatedRows = [...state.rows]
  if (previousColumnKind !== column.kind) {
    // Remove filters, colours and row-locks if kind changes
    filters = state.userConfiguration.filterSettings.filter(
      (filterSetting) => filterSetting.columnId != column.publicId
    )
    colours = state.userConfiguration.colourSettings.filter(
      (colourSetting) => colourSetting.columnId != column.publicId
    )
    rowLocks = state.userConfiguration.rowLockSettings.filter((lockSetting) => lockSetting.columnId != column.publicId)

    // Reshape cached cell values for the new kind, and give the column store a fresh strategy
    // so the rows_updated refresh can't match a stale dictionary entry (["A"] and "A" share a key)
    const columnIndex = state.columnIdToIndex.get(column.publicId)
    if (columnIndex !== undefined) {
      const transform = (value: ICellValue): ICellValue =>
        convertCachedCellValue(value, previousColumnKind, column.kind)

      state.columnStore?.replaceColumnKind(columnIndex, column.kind, transform)
      // rows carrying their own rowData are read from it even when a column store exists
      for (let i = 0; i < updatedRows.length; i++) {
        const row = updatedRows[i]
        if (!row.rowData) continue
        const newRowData = [...row.rowData]
        newRowData[columnIndex] = transform(newRowData[columnIndex])
        updatedRows[i] = { ...row, rowData: newRowData }
      }
    }
  }

  if (previousColumnName !== column.name) {
    // Update names in filters/sort/group/colours/groups
    filters = state.userConfiguration.filterSettings.map((settings) => {
      if (settings.columnId == column.publicId) {
        settings.columnName = column.name
      }
      return settings
    })

    groups = state.userConfiguration.groupSettings.map((settings) => {
      if (settings.columnId == column.publicId) {
        settings.columnName = column.name
      }
      return settings
    })

    sorts = state.userConfiguration.sortSettings.map((settings) => {
      if (settings.columnId == column.publicId) {
        settings.columnName = column.name
      }
      return settings
    })

    colours = state.userConfiguration.colourSettings.map((settings) => {
      if (settings.columnId == column.publicId) {
        settings.columnName = column.name
      }
      return settings
    })

    rowLocks = state.userConfiguration.rowLockSettings.map((settings) => {
      if (settings.columnId == column.publicId) {
        settings.columnName = column.name
      }
      return settings
    })
  }

  // Go through each of the views and update the columns if you need to
  const newViews = [...state.tableView]
  newViews.forEach((view: ITableViewWithColumns, viewNumber: number) => {
    newViews[viewNumber].columns = view.columns.map((viewColumn: ITableViewColumn) =>
      viewColumn.publicId === column.publicId
        ? {
            ...viewColumn,
            name: column.name,
            kind: column.kind,
            kindOptions: column.kindOptions,
            description: column.description,
            script: column.script,
            width: column.width,
            thousandSeparator: column.thousandSeparator,
            displayLink: column.displayLink,
            decimalPlaces: column.decimalPlaces,
            dateFormat: column.dateFormat,
            defaultValue: column.defaultValue,
            scriptEnabled: column.scriptEnabled,
            formula: column.formula,
            formulaEnabled: column.formulaEnabled,
            aggregate: column.aggregate,
            stringValidation:
              state.tableDetails.keepValidationsInSync || view.publicId === state.activeTableView
                ? column.stringValidation
                : viewColumn.stringValidation,
            validationMessage:
              state.tableDetails.keepValidationsInSync || view.publicId === state.activeTableView
                ? column.validationMessage
                : viewColumn.validationMessage,
            hardValidation:
              state.tableDetails.keepValidationsInSync || view.publicId === state.activeTableView
                ? column.hardValidation
                : viewColumn.hardValidation,
            validationNoBlanks:
              state.tableDetails.keepValidationsInSync || view.publicId === state.activeTableView
                ? column.validationNoBlanks
                : viewColumn.validationNoBlanks,
            validationNoDuplicates:
              state.tableDetails.keepValidationsInSync || view.publicId === state.activeTableView
                ? column.validationNoDuplicates
                : viewColumn.validationNoDuplicates,
            displayValidationError:
              state.tableDetails.keepValidationsInSync || view.publicId === state.activeTableView
                ? column.displayValidationError
                : viewColumn.displayValidationError,
            blockInvalidSelectValues:
              state.tableDetails.keepValidationsInSync || view.publicId === state.activeTableView
                ? column.blockInvalidSelectValues
                : viewColumn.blockInvalidSelectValues,
            headerBackgroundColor: column.headerBackgroundColor,
            headerTextColor: column.headerTextColor,
            exportWidth: column.exportWidth,
            formWidth: view.publicId === state.activeTableView ? column.formWidth : viewColumn.formWidth
          }
        : previousColumnName !== column.name && viewColumn.formula
          ? {
              ...viewColumn,
              formula: viewColumn.formula.replaceAll(prevColumnNameFormula, newColumnNameFormula)
            }
          : viewColumn
    )

    if (previousColumnKind !== column.kind) {
      // Remove filters and colours if kind changes
      newViews[viewNumber].filterSettings = view.filterSettings.filter(
        (filter: ITableViewFilter) => filter.columnId !== column.publicId
      )
      newViews[viewNumber].colourSettings = view.colourSettings.filter(
        (colour: ITableViewColour) => colour.columnId !== column.publicId
      )
    } else if (previousColumnName !== column.name) {
      // Update names in filters/sort/group/colours/groups
      newViews[viewNumber].colourSettings = view.colourSettings.map((settings) => {
        if (settings.columnId == column.publicId) {
          settings.columnName = column.name
        }
        return settings
      })

      newViews[viewNumber].groupSettings = view.groupSettings.map((settings) => {
        if (settings.columnId == column.publicId) {
          settings.columnName = column.name
        }
        return settings
      })

      newViews[viewNumber].filterSettings = view.filterSettings.map((settings) => {
        if (settings.columnId == column.publicId) {
          settings.columnName = column.name
        }
        return settings
      })

      newViews[viewNumber].sortSettings = view.sortSettings.map((settings) => {
        if (settings.columnId == column.publicId) {
          settings.columnName = column.name
        }
        return settings
      })
    }
  })

  // Go through each of the columns
  const newTableDetails = state.tableDetails
  newTableDetails.columns = newTableDetails.columns.map((tableColumn: ITableColumn) => {
    return tableColumn.publicId === column.publicId
      ? {
          ...tableColumn,
          name: column.name,
          description: column.description,
          kind: column.kind,
          kindOptions: column.kindOptions,
          script: column.script,
          scriptEnabled: column.scriptEnabled,
          formula: column.formula,
          formulaEnabled: column.formulaEnabled,
          aggregate: column.aggregate,
          thousandSeparator: column.thousandSeparator,
          displayLink: column.displayLink,
          decimalPlaces: column.decimalPlaces,
          dateFormat: column.dateFormat,
          defaultValue: column.defaultValue,
          headerBackgroundColor: column.headerBackgroundColor,
          headerTextColor: column.headerTextColor,
          exportWidth: column.exportWidth
        }
      : previousColumnName !== column.name && tableColumn.formula
        ? {
            ...tableColumn,
            formula: tableColumn.formula.replaceAll(prevColumnNameFormula, newColumnNameFormula)
          }
        : tableColumn
  })

  // Handle case where validationNoDuplicates was just enabled on this column
  // We need to build the columnValuesCount for this column by scanning all rows.
  // A kind change reshapes the cached values (2.7 -> 3, a list -> its items) and can flip the tally
  // between folded and exact keys (text <-> email), which leaves the existing counts stale even
  // though validation never turned off - rebuild those too, from the reshaped rows.
  let newColumnValuesCount = state.columnValuesCount
  if (column.validationNoDuplicates && (!previousValidationNoDuplicates || previousColumnKind !== column.kind)) {
    newColumnValuesCount = { ...state.columnValuesCount }
    newColumnValuesCount[column.publicId] = {}
    const foldCase = isCaseInsensitiveDuplicateKind(column.kind)
    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i]
      const value = getRowValue(row, column.publicId, state.columnIdToIndex, state.columnStore)
      if (value !== undefined && value !== null) {
        const valueString = getDuplicateValueKey(value.toString(), foldCase)
        if (newColumnValuesCount[column.publicId][valueString] !== undefined) {
          newColumnValuesCount[column.publicId][valueString] += 1
        } else {
          newColumnValuesCount[column.publicId][valueString] = 1
        }
      }
    }
  } else if (!column.validationNoDuplicates && previousValidationNoDuplicates) {
    // validationNoDuplicates was disabled, remove the column from tracking
    newColumnValuesCount = { ...state.columnValuesCount }
    delete newColumnValuesCount[column.publicId]
  }

  return {
    ...state,
    refreshing: false,
    rows: updatedRows,
    tableView: newViews,
    viewDetails: { ...state.viewDetails, columns: newColumns },
    columnValuesCount: newColumnValuesCount,
    userConfiguration: {
      ...state.userConfiguration,
      filterSettings: filters,
      colourSettings: colours,
      rowLockSettings: rowLocks,
      sortSettings: sorts,
      groupSettings: groups
    }
  }
}

const updateRows = (state: ISpreadsheetData, rows: ITableRow[]) => {
  // Rows arriving here come from the `rows_updated` socket event. Unlike editCell/updateCells,
  // this path bypasses the interactive edit reducers, so keep columnValuesCount in sync here or
  // duplicate validation reads stale counts until a page reload rebuilds them.
  const trackedColumnIds = Object.keys(state.columnValuesCount)
  const hasTrackedColumns = trackedColumnIds.length > 0
  const caseInsensitiveColumns = hasTrackedColumns
    ? caseInsensitiveDuplicateColumns(state)
    : NO_CASE_INSENSITIVE_COLUMNS
  // Shallow-copy the outer map only, matching editCell/insertRow/deleteRows: we mutate the inner
  // per-column counts in place below and return this new outer object so React sees the change.
  // Deep-copying every tracked column up front is wasteful on large tables (these maps can hold
  // thousands of keys) and rows_updated can fire often.
  const newColumnValuesCount = { ...state.columnValuesCount }
  const matched = new Set<string>()

  const updatedRows = state.rows.map((r) => {
    const updatedRow = rows.find((row) => row.publicId === r.publicId)
    if (!updatedRow) return r
    matched.add(r.publicId)

    // Keep the duplicate tallies correct: decrement the old value's count and increment the new.
    if (hasTrackedColumns && updatedRow.rowData) {
      for (let t = 0; t < trackedColumnIds.length; t++) {
        const columnId = trackedColumnIds[t]
        const columnIndex = state.columnIdToIndex.get(columnId)

        if (columnIndex === undefined) continue

        const counts = newColumnValuesCount[columnId]

        if (!counts) continue

        const oldValue = getRowValue(r, columnId, state.columnIdToIndex, state.columnStore)
        const newValue: ICellValue = updatedRow.rowData[columnIndex]
        const foldCase = caseInsensitiveColumns.has(columnId)
        const oldKey = getDuplicateValueKey(oldValue?.toString() ?? '', foldCase)
        if (counts[oldKey] !== undefined) counts[oldKey] -= 1
        const newKey = getDuplicateValueKey(newValue?.toString() ?? '', foldCase)
        counts[newKey] = (counts[newKey] ?? 0) + 1
      }
    }

    if (state.columnStore && r._rowIndex >= 0 && updatedRow.rowData) {
      // Write updated values into the store instead of attaching rowData
      for (let i = 0; i < updatedRow.rowData.length; i++) {
        state.columnStore.set(r._rowIndex, i, updatedRow.rowData[i])
      }
      return { ...r, sortOrder: updatedRow.sortOrder }
    }

    const newRowData = [...(updatedRow.rowData ?? [])]
    return { ...r, ...updatedRow, rowData: newRowData }
  })

  // While the stream is still running, a row not here yet may still be on its way: keep its latest
  // version for when it lands (see applyPendingStreamChanges).
  let streamPendingUpdates = state.streamPendingUpdates
  if (!state.rowStreamComplete) {
    const pending = rows.filter((row) => !matched.has(row.publicId))
    if (pending.length > 0) {
      streamPendingUpdates = new Map(streamPendingUpdates)
      pending.forEach((row) => streamPendingUpdates!.set(row.publicId, row))
    }
  }

  return {
    ...state,
    rows: updatedRows,
    columnValuesCount: newColumnValuesCount,
    streamPendingUpdates,
    dataVersion: state.dataVersion + 1
  }
}

const insertRow = (state: ISpreadsheetData, row: ITableRow, nextRowPublicId?: string) => {
  const newColumnValuesCount = { ...state.columnValuesCount }
  const trackedColumnIds = Object.keys(state.columnValuesCount)
  const caseInsensitiveColumns =
    trackedColumnIds.length > 0 ? caseInsensitiveDuplicateColumns(state) : NO_CASE_INSENSITIVE_COLUMNS
  for (let i = 0; i < trackedColumnIds.length; i++) {
    const columnId = trackedColumnIds[i]
    const value = getRowValue(row, columnId, state.columnIdToIndex, state.columnStore)
    if (value) {
      const valueKey = getDuplicateValueKey(value.toString(), caseInsensitiveColumns.has(columnId))
      if (newColumnValuesCount[columnId][valueKey]) newColumnValuesCount[columnId][valueKey] += 1
      else {
        newColumnValuesCount[columnId][valueKey] = 1
      }
    }
  }

  const newRowSortOrder = row.sortOrder
  let isReindexed = false

  if (nextRowPublicId && state.rows.length > 0) {
    const targetRow = state.rows.find((r) => r.publicId === nextRowPublicId)
    if (targetRow) {
      // Detect reindexing by comparing the target row's old sortOrder with the new row's sortOrder.
      // If there's a huge jump (e.g., from 2 to 2500), the backend reindexed all sort orders.
      isReindexed = newRowSortOrder - targetRow.sortOrder >= 1000
    }
  }

  // Insert the new row relative to the CURRENT row ordering rather than re-sorting.
  // state.rows is already held in display order (it's re-sorted whenever the user changes
  // sortSettings — see the filter/sort effect in the data context), so splicing the new
  // row immediately before nextRowPublicId keeps it exactly where the user clicked
  // "Insert Row Above", whether the table is in default order or a custom sort is applied.
  // Previously we re-sorted by sortSettings here, which placed the (blank-valued) new row
  // wherever its empty sort-column values happened to fall — making it appear in a random
  // location when the table was sorted (#10270).
  let insertIndex = state.rows.length
  if (nextRowPublicId) {
    const targetIndex = state.rows.findIndex((r) => r.publicId === nextRowPublicId)
    if (targetIndex !== -1) {
      insertIndex = targetIndex
    }
  }
  let newRows = [...state.rows.slice(0, insertIndex), row, ...state.rows.slice(insertIndex)]

  // When the backend reindexed sort orders, normalise EVERY row's sortOrder (the new row
  // included) to match the final array order. Splicing positionally leaves the new row's
  // backend sortOrder potentially inconsistent with the synthetic values of its neighbours;
  // without this, clearing the sort later (which re-sorts by sortOrder) would move the new
  // row away from where it was inserted, contradicting "Insert Row Above".
  if (isReindexed) {
    newRows = newRows.map((r, index) => ({ ...r, sortOrder: (index + 1) * 1000 }))
  }

  // Reconstruct bitmap: preserve old visibility for existing rows,
  // always mark the new row as visible (user just created it).
  let newBitmap: Uint8Array | null = null
  let newVisibleCount = newRows.length
  if (state.visibleBitmap) {
    const visibleIds = new Set<string>()
    for (let i = 0; i < state.rows.length; i++) {
      if (state.visibleBitmap[i]) visibleIds.add(state.rows[i].publicId)
    }
    visibleIds.add(row.publicId)

    newBitmap = new Uint8Array(newRows.length)
    newVisibleCount = 0
    for (let i = 0; i < newRows.length; i++) {
      if (visibleIds.has(newRows[i].publicId)) {
        newBitmap[i] = 1
        newVisibleCount++
      }
    }
  }

  return {
    ...state,
    rows: newRows,
    columnValuesCount: newColumnValuesCount,
    totalRows: state.totalRows + 1,
    totalRowsOnServer: state.totalRowsOnServer + 1,
    visibleBitmap: newBitmap,
    visibleCount: newVisibleCount
  }
}

const appendRows = (state: ISpreadsheetData, rows: ITableRow[]) => {
  const newColumnValuesCount = { ...state.columnValuesCount }
  const trackedColumnIds = Object.keys(state.columnValuesCount)

  // Only update counts for columns that are being tracked (have duplicate validation)
  if (trackedColumnIds.length > 0) {
    const caseInsensitiveColumns = caseInsensitiveDuplicateColumns(state)
    for (let j = 0; j < rows.length; j++) {
      const row = rows[j]
      for (let i = 0; i < trackedColumnIds.length; i++) {
        const columnId = trackedColumnIds[i]
        const value = getRowValue(row, columnId, state.columnIdToIndex, state.columnStore)
        if (value) {
          const valueKey = getDuplicateValueKey(value.toString(), caseInsensitiveColumns.has(columnId))
          if (newColumnValuesCount[columnId][valueKey]) newColumnValuesCount[columnId][valueKey] += 1
          else {
            newColumnValuesCount[columnId][valueKey] = 1
          }
        }
      }
    }
  }

  const newRows = [...state.rows, ...rows]
  // Extend bitmap for new rows (mark as visible), or invalidate if no bitmap active
  let newBitmap = state.visibleBitmap
  const newVisibleCount = state.visibleCount + rows.length
  if (state.visibleBitmap) {
    const extended = new Uint8Array(newRows.length)
    extended.set(state.visibleBitmap)
    // New rows default to visible (1)
    for (let i = state.visibleBitmap.length; i < extended.length; i++) {
      extended[i] = 1
    }
    newBitmap = extended
  }

  return {
    ...state,
    columnValuesCount: newColumnValuesCount,
    rows: newRows,
    totalRows: state.totalRows + rows.length,
    totalRowsOnServer: state.totalRowsOnServer + rows.length,
    visibleBitmap: newBitmap,
    visibleCount: newVisibleCount
  }
}

const deleteRows = (state: ISpreadsheetData, rowIds: string[]) => {
  const newColumnValuesCount = { ...state.columnValuesCount }
  const trackedColumnIds = Object.keys(state.columnValuesCount)
  const rowIdSet = new Set(rowIds)
  const deletedIds = new Set<string>()
  const hasTrackedColumns = trackedColumnIds.length > 0
  const caseInsensitiveColumns = hasTrackedColumns
    ? caseInsensitiveDuplicateColumns(state)
    : NO_CASE_INSENSITIVE_COLUMNS
  const hasColumnStore = !!state.columnStore
  const storeRowIndices: number[] = []

  // Single pass over rows: build filtered array, update bitmap, track column value counts,
  // and collect columnStore row indices for tombstoning
  const newRows: ITableRow[] = []
  let newBitmap: Uint8Array | null = null
  const bitmapBuf = state.visibleBitmap ? new Uint8Array(state.rows.length) : null
  let kept = 0
  let bitmapVisibleCount = 0

  for (let i = 0; i < state.rows.length; i++) {
    const row = state.rows[i]
    if (rowIdSet.has(row.publicId)) {
      deletedIds.add(row.publicId)
      // Row is being deleted — update tracked column counts and collect store indices
      if (hasTrackedColumns) {
        for (let j = 0; j < trackedColumnIds.length; j++) {
          const columnId = trackedColumnIds[j]
          const value = getRowValue(row, columnId, state.columnIdToIndex, state.columnStore)
          const valueKey = getDuplicateValueKey(String(value ?? ''), caseInsensitiveColumns.has(columnId))
          if (
            value !== undefined &&
            value !== null &&
            newColumnValuesCount[columnId] &&
            newColumnValuesCount[columnId][valueKey] !== undefined
          ) {
            newColumnValuesCount[columnId][valueKey] -= 1
          }
        }
      }
      if (hasColumnStore && row._rowIndex >= 0) {
        storeRowIndices.push(row._rowIndex)
      }
    } else {
      newRows.push(row)
      if (bitmapBuf) {
        bitmapBuf[kept] = state.visibleBitmap![i]
        bitmapVisibleCount += state.visibleBitmap![i]
      }
      kept++
    }
  }

  if (hasColumnStore) {
    state.columnStore!.deleteRows(storeRowIndices)
  }

  if (bitmapBuf) {
    newBitmap = bitmapBuf.subarray(0, kept)
  }

  const visibleCount = newBitmap ? bitmapVisibleCount : newRows.length
  const actualDeleted = state.rows.length - newRows.length

  // While the stream is still running, an id not here yet may still be on its way (the table was
  // truncated, say): remember it so the row is dropped when it lands (see applyPendingStreamChanges).
  let streamPendingDeletes = state.streamPendingDeletes
  if (!state.rowStreamComplete) {
    const pending = rowIds.filter((id) => !deletedIds.has(id))
    if (pending.length > 0) {
      streamPendingDeletes = new Set(streamPendingDeletes)
      pending.forEach((id) => streamPendingDeletes!.add(id))
    }
  }

  return {
    ...state,
    columnValuesCount: newColumnValuesCount,
    totalRows: state.totalRows - actualDeleted,
    totalRowsOnServer: state.totalRowsOnServer - actualDeleted,
    rows: newRows,
    visibleBitmap: newBitmap,
    visibleCount,
    streamPendingDeletes
  }
}

const commentThreadsChange = (
  state: ISpreadsheetData,
  rowId: string,
  noOpenCommentThreads: number,
  noResolvedCommentThreads: number
) => {
  const newCommentThreads = [...state.comments]
  const currentCommentThread = newCommentThreads.find(
    (commentThread: ICommentThreadStats) => commentThread.referenceId === rowId
  )
  if (currentCommentThread) {
    currentCommentThread.openCommentThreads = noOpenCommentThreads
    currentCommentThread.resolvedCommentThreads = noResolvedCommentThreads
  } else {
    newCommentThreads.push({
      openCommentThreads: noOpenCommentThreads,
      referenceId: rowId,
      resolvedCommentThreads: noResolvedCommentThreads
    })
  }

  return {
    ...state,
    comments: newCommentThreads
  }
}

const toggleCollapsedGroupedColumns = (state: ISpreadsheetData, columnId: string, rows: ITableRow[]) => {
  const collapsedColumnIds = new Set(state.collapsedColumnIds)
  const collapsedGroupIds = new Set(state.collapsedGroupIds)

  // This works out the group keys for the column
  const toGroupColumns = state.userConfiguration.groupSettings
    .map((toGroupColumn) => {
      const column = state.viewDetails.columns.find((c) => c.publicId === toGroupColumn.columnId)
      return column
    })
    .filter((column): column is NonNullable<typeof column> => column != null)
  const groupKeys = getGroupingKeysForColumn(rows, toGroupColumns, columnId, state.columnIdToIndex, state.columnStore)

  if (collapsedColumnIds.has(columnId)) {
    collapsedColumnIds.delete(columnId)
    for (const key of groupKeys) {
      collapsedGroupIds.delete(key)
    }
  } else {
    collapsedColumnIds.add(columnId)
    for (const key of groupKeys) {
      collapsedGroupIds.add(key)
    }
  }

  return { ...state, collapsedColumnIds, collapsedGroupIds }
}

const searchTable = (state: ISpreadsheetData, searchTerm: string) => {
  const searchedRows = new Map<string, string[]>()
  let found = 0

  // With bitmap model, all rows (visible + filtered) live in state.rows.
  // No merge needed.
  const normalizedSearchTerm = searchTerm.toLowerCase()
  const bitmap = state.visibleBitmap

  const columns = state.viewDetails.columns.filter(
    (column: ITableViewColumn) =>
      !state.userConfiguration.hiddenColumns.includes(column.publicId) && !BLOCK_SEARCH_BY.includes(column.kind)
  )

  for (let i = 0; i < state.rows.length; i++) {
    const row = state.rows[i]
    const matchedColumns: string[] = []

    for (let j = 0; j < columns.length; j++) {
      const key = columns[j].publicId
      const value = getRowValue(row, key, state.columnIdToIndex, state.columnStore)?.toString().toLowerCase()
      if (value?.includes(normalizedSearchTerm)) {
        matchedColumns.push(key)
        // Only count matches from visible rows so the count reflects what the user can navigate to
        const isVisible = !bitmap || bitmap[i]
        if (isVisible) {
          found += 1
        }
      }
    }

    if (matchedColumns.length > 0) {
      searchedRows.set(row.publicId, matchedColumns)
    }
  }

  // Only update search state - actual row filtering is handled by filterRows() in context
  return {
    ...state,
    searchedRows,
    searchTerm,
    searchFound: found
  }
}

const bulkEditCells = (state: ISpreadsheetData, cells: ICellUpdateAction[]) => {
  // convert to rowId -> ICellUpdateAction map for faster lookup in the rows map()
  // function below
  const seenColumns = new Set<string>()
  const rowIdToCellUpdate = cells.reduce(
    (prev, current) => {
      // Merge together rows, as there could be multiple updates to
      // the same row
      seenColumns.add(current.columnId)
      if (prev[current.rowId]) {
        prev[current.rowId].value = {
          ...prev[current.rowId].value,
          ...current.value
        }
      } else {
        prev[current.rowId] = current
      }
      return prev
    },
    {} as Record<string, ICellUpdateAction>
  )

  const newRows: ITableRow[] = state.rows.map((row: ITableRow) => {
    if (rowIdToCellUpdate[row.publicId]) {
      const updates = rowIdToCellUpdate[row.publicId].value
      if (state.columnStore && row._rowIndex >= 0) {
        // Write updates directly to the column store
        Object.keys(updates).forEach((columnId) => {
          const columnIndex = state.columnIdToIndex.get(columnId)
          if (columnIndex !== undefined) {
            state.columnStore!.set(row._rowIndex, columnIndex, updates[columnId])
          }
        })
        return row
      }
      const newRowData = [...(row.rowData ?? [])]
      // Update each column value in the array
      Object.keys(updates).forEach((columnId) => {
        const columnIndex = state.columnIdToIndex.get(columnId)
        if (columnIndex !== undefined) {
          newRowData[columnIndex] = updates[columnId]
        }
      })
      return { ...row, rowData: newRowData }
    }
    return row // Return reference, don't clone unmodified rows
  })

  const newColumnValuesCount = { ...state.columnValuesCount }

  // Only rebuild counts for columns that are being tracked (have duplicate validation)
  // and were affected by this bulk edit
  const trackedColumnsAffected = Array.from(seenColumns).filter((columnId) => columnId in state.columnValuesCount)

  if (trackedColumnsAffected.length > 0) {
    const caseInsensitiveColumns = caseInsensitiveDuplicateColumns(state)

    // Clear counts for affected columns
    trackedColumnsAffected.forEach((columnId: string) => {
      newColumnValuesCount[columnId] = {}
    })

    // Rebuild counts from the new rows
    newRows.forEach((row) => {
      trackedColumnsAffected.forEach((columnId: string) => {
        const rowValue = getRowValue(row, columnId, state.columnIdToIndex, state.columnStore) as string
        const valueKey = getDuplicateValueKey(rowValue, caseInsensitiveColumns.has(columnId))
        if (newColumnValuesCount[columnId][valueKey] !== undefined) {
          newColumnValuesCount[columnId][valueKey] += 1
        } else {
          newColumnValuesCount[columnId][valueKey] = 1
        }
      })
    })
  }

  return {
    ...state,
    rows: newRows,
    refreshing: false,
    columnValuesCount: newColumnValuesCount,
    dataVersion: state.dataVersion + 1
  }
}

const addHistory = (state: ISpreadsheetData, event: ITableViewHistory) => {
  let newHistory = [...state.history.slice(0, state.historyPoint + 1)]
  newHistory = newHistory.concat(event)
  const newHistoryPoint = newHistory.length - 1
  return { ...state, history: newHistory, historyPoint: newHistoryPoint }
}

const refreshSelectOptions = (state: ISpreadsheetData, view: ITableViewWithColumns) => {
  //Update columns of all views
  const newViews = state.tableView.map((tableView: ITableViewWithColumns) => {
    const newView = { ...tableView }
    const newColumns = newView.columns.map((currentColumn: ITableViewColumn) => {
      const foundColumn = view.columns.find(
        (newColumn: ITableViewColumn) => newColumn.publicId === currentColumn.publicId
      )
      if (foundColumn) {
        return { ...foundColumn }
      }
      return currentColumn
    })
    return { ...newView, columns: newColumns }
  })

  const viewColumns = state.viewDetails.columns.map((currentColumn: ITableViewColumn) => {
    const foundColumn = view.columns.find(
      (newColumn: ITableViewColumn) => newColumn.publicId === currentColumn.publicId
    )
    if (foundColumn) {
      return { ...foundColumn }
    }
    return currentColumn
  })
  return { ...state, tableView: newViews, viewDetails: { ...state.viewDetails, columns: viewColumns } }
}

export const spreadsheetDataReducer = (
  state: ISpreadsheetData,
  action: SpreadsheetReducerActions
): ISpreadsheetData => {
  switch (action.type) {
    case 'CHANGE_COLUMN_WIDTH':
      return changeColumnWidth(state, action.columnId, action.columnWidth)
    case 'CHANGE_TRANSPOSE_COLUMN_WIDTH':
      return changeTransposeColumnWidth(state, action.columnId, action.width)
    case 'CHANGE_MATRIX_COLUMN_WIDTH':
      return changeMatrixColumnWidth(state, action.columnId, action.width)
    case 'CHANGE_COLUMN_AGGREGATE':
      return changeColumnAggregate(state, action.columnId, action.aggregate)
    case 'SET_SPREADSHEET_DATA':
      return action.spreadsheetData
    case 'FINISH_LOADING':
      return {
        ...action.spreadsheetData,
        columnValuesCount: action.columnValuesCount,
        loading: true,
        loadingProgress: 100,
        rowStreamComplete: true
      }
    case 'SET_SPREADSHEET_ROWS':
      return setRows(state, action.rows, action.totalRows, action.visibleBitmap, action.visibleCount)
    case 'SET_COLUMN_VALUES_COUNT':
      return { ...state, columnValuesCount: action.columnValuesCount }
    case 'ADD_COLUMN':
      return createTableColumn(state, action.column)
    case 'UPDATE_COLUMN':
      return updateTableColumn(state, action.column)
    case 'DELETE_COLUMN':
      return deleteTableColumn(state, action.columnId)
    case 'DELETE_COLUMNS':
      return deleteTableColumns(state, action.columnIds)
    case 'SHOW_COLUMN':
      return showColumn(state, action.columnId)
    case 'HIDE_COLUMN':
      return hideColumn(state, action.columnId)
    case 'HIDE_ALL_COLUMNS':
      return hideAllColumns(state)
    case 'SHOW_ALL_COLUMNS':
      return showAllColumns(state)
    case 'REQUIRE_COLUMN':
      return requireColumn(state, action.columnId)
    case 'UNREQUIRE_COLUMN':
      return unrequireColumn(state, action.columnId)
    case 'REQUIRE_ALL_COLUMNS':
      return requireAllColumns(state)
    case 'UNREQUIRE_ALL_COLUMNS':
      return unrequireAllColumns(state)
    case 'CHANGE_COLUMN_ORDER':
      return changeColumnOrder(state, action.columnId, action.newIndex)
    case 'CHANGE_ALL_COLUMNS_ORDER':
      return changeAllColumnsOrder(state, action.columns)
    case 'CHANGE_ROW_ORDER':
      return changeRowOrder(state, action.rowId, action.newIndex, action.newSortOrder)
    case 'EDIT_CELL':
      return editCell(state, action.columnId, action.rowId, action.value, action.oldValue)
    case 'CHANGE_ROW_HEIGHT':
      return {
        ...state,
        viewDetails: { ...state.viewDetails, rowHeight: action.newRowHeight },
        userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
      }
    case 'REFRESH':
      return { ...state, refreshing: action.refreshing, busyLabel: action.refreshing ? (action.label ?? null) : null }
    case 'LOADING':
      return {
        ...state,
        loading: action.loading,
        loadingProgress: action.loading ? 0 : state.loadingProgress,
        rowStreamComplete: action.loading ? false : state.rowStreamComplete
      }
    case 'SET_LOADING_PROGRESS':
      return { ...state, loadingProgress: action.loadingProgress }
    case 'START_ROW_STREAM_RENDER':
      // First batch of a progressive stream: render the grid now, rows still arriving.
      return {
        ...action.spreadsheetData,
        columnValuesCount: action.columnValuesCount,
        loading: false,
        loadingProgress: action.loadingProgress,
        rowStreamComplete: false
      }
    case 'STREAM_ROWS_PROGRESS':
      return {
        ...appendStreamedRows(state, action.rows, action.columnValuesCount),
        loadingProgress: action.loadingProgress
      }
    case 'FINISH_ROW_STREAM':
      // End of a progressive stream: keep the grid (and whatever the user did meanwhile) mounted;
      // the rowStreamComplete effect applies the final sort/filter pass.
      return {
        ...appendStreamedRows(state, action.rows, action.columnValuesCount),
        loadingProgress: 100,
        rowStreamComplete: true,
        streamPendingDeletes: null,
        streamPendingUpdates: null
      }
    case 'ADD_SORT':
      return addSort(state, action.columnId, action.columnName)
    case 'DELETE_SORT':
      return deleteSort(state, action.columnId)
    case 'CHANGE_SORT_DIRECTION':
      return changeSortDirection(state, action.columnId, action.newDirection)
    case 'CHANGE_SORT_ORDER':
      return changeSortOrder(state, action.existingIndex, action.newIndex)
    case 'CHANGE_FROZEN_INDEX':
      return changeFrozenIndex(state, action.frozenIndex)
    case 'CHANGE_DISPLAY_VALIDATION_ROWS':
      return changeDisplayValidationRows(state, action.displayValidationErrorRows)
    case 'CHANGE_DISPLAY_COMMENT_ROWS':
      return changeDisplayCommentRows(state, action.displayCommentRows)
    case 'CHANGE_COLLAPSED_GROUP_VIEW':
      return changeCollapsedGroupView(state, action.collapsedGroupView)
    case 'CHANGE_TREE_GROUP_VIEW':
      return changeTreeGroupView(state, action.treeGroupView)
    case 'CHANGE_UNPACK_MULTISELECT_GROUP_VIEW':
      return changeUnpackMultiselectGroupView(state, action.unpackMultiselectGroupView)
    case 'UPDATE_VIEW':
      return updateView(state, action.field, action.viewId, action.value)
    case 'UPDATE_TABLE':
      return updateTable(state, action.field, action.value)
    case 'UPDATE_TABLE_JOINS':
      return updateTableJoins(state, action.table)
    case 'SET_VIEW':
      return setView(state, action.viewId, action.inProcess, action.viewDetails)
    case 'SET_PIVOT_COLUMN': {
      const newHidden = action.pivotColumnId
        ? state.userConfiguration.hiddenColumns.filter((id) => id !== action.pivotColumnId)
        : state.userConfiguration.hiddenColumns
      const previous = state.viewDetails.transposeSettings ?? EMPTY_TRANSPOSE_SETTINGS
      return {
        ...state,
        viewDetails: {
          ...state.viewDetails,
          transposeSettings: { ...previous, pivotColumnId: action.pivotColumnId }
        },
        userConfiguration: { ...state.userConfiguration, hiddenColumns: newHidden, unsavedChanges: true }
      }
    }
    case 'SET_MATRIX_SETTINGS': {
      return {
        ...state,
        viewDetails: {
          ...state.viewDetails,
          matrixSettings: action.matrixSettings
        },
        userConfiguration: { ...state.userConfiguration, unsavedChanges: true }
      }
    }
    case 'HYDRATE_VIEW':
      return mergeFetchedView(state, action.viewDetails)
    case 'ADD_VIEW':
      return addView(state, action.view, action.inProcess)
    case 'SAVE_CURRENT_VIEW':
      return saveCurrentView(state)
    case 'DELETE_VIEW':
      return deleteView(state, action.viewId)
    case 'EXPAND_ALL_GROUPS':
      return expandAllGroups(state)
    case 'COLLAPSE_ALL_GROUPS':
      return collapseAllGroups(state, action.groupIds)
    case 'TOGGLE_GROUP_COLLAPSE':
      return toggleGroupCollapse(state, action.groupId)
    case 'ADD_GROUP':
      return addGroup(state, action.columnId, action.columnName)
    case 'ADD_CHART':
      return addChart(state, action.chart)
    case 'SET_CUSTOM_VIEW': {
      const setPage = <T extends ITableViewWithColumns>(view: T): T =>
        view.publicId === action.viewId ? { ...view, customView: action.customView } : view
      return { ...state, viewDetails: setPage(state.viewDetails), tableView: state.tableView.map(setPage) }
    }
    case 'SET_CUSTOM_VIEW_DRAFT': {
      const setDraft = <T extends ITableViewWithColumns>(view: T): T =>
        view.publicId === action.viewId ? { ...view, customViewDraft: action.draft } : view
      return { ...state, viewDetails: setDraft(state.viewDetails), tableView: state.tableView.map(setDraft) }
    }
    case 'SET_CALENDAR_SETTINGS':
      return {
        ...state,
        userConfiguration: {
          ...state.userConfiguration,
          calendarSettings: action.calendarSettings,
          unsavedChanges: true
        }
      }
    case 'DELETE_GROUP':
      return deleteGroup(state, action.columnId)
    case 'CHANGE_GROUP_DIRECTION':
      return changeGroupDirection(state, action.columnId, action.newDirection)
    case 'CHANGE_GROUP_ORDER':
      return changeGroupOrder(state, action.existingIndex, action.newIndex)
    case 'CHANGE_GROUP_COLUMN':
      return changeGroupColumn(state, action.index, action.columnId, action.columnName)
    case 'UPDATE_FILTERS':
      return isForAnotherView(state, action.viewId) ? state : updateFilters(state, action.filters)
    case 'UPDATE_COLOURS':
      return isForAnotherView(state, action.viewId) ? state : updateColours(state, action.colours)
    case 'UPDATE_LOCKS':
      return isForAnotherView(state, action.viewId) ? state : updateLocks(state, action.locks)
    case 'REFRESH_SPREADSHEET_ROWS':
      return refreshSpreadsheetRows(state, action.rows, action.totalRows)
    case 'APPEND_SPREADSHEET_ROWS':
      return appendSpreadsheetRows(state, action.rows)
    case 'UPDATE_ROWS':
      return updateRows(state, action.rows)
    case 'INSERT_ROW':
      return insertRow(state, action.row, action.nextRowPublicId)
    case 'APPEND_ROWS':
      return appendRows(state, action.rows)
    case 'DELETE_ROWS':
      return deleteRows(state, action.rowIds)
    case 'COMMENT_THREADS_CHANGE':
      return commentThreadsChange(state, action.rowId, action.noOpenCommentThreads, action.noResolvedCommentThreads)
    case 'TOGGLE_COLUMN_COLLAPSE':
      return toggleCollapsedGroupedColumns(state, action.columnId, action.rows)
    case 'SEARCH_TABLE':
      return searchTable(state, action.searchTerm)
    case 'CLEAR_SEARCH':
      return {
        ...state,
        searchTerm: '',
        selectedSearchCell: INITIAL_SELECTED_SEARCH_CELL,
        searchedRows: new Map<string, string[]>()
      }
    case 'SET_SELECTED_SEARCH_CELL':
      return {
        ...state,
        selectedSearchCell: {
          rowId: action.rowId,
          columnId: action.columnId,
          columnNumber: action.columnNumber,
          rowNumber: action.rowNumber
        }
      }
    case 'BULK_EDIT_CELL':
      return bulkEditCells(state, action.cells)
    case 'ADD_HISTORY':
      return addHistory(state, action.event)
    case 'UPDATE_HISTORY_POINT':
      return { ...state, historyPoint: action.point }
    case 'REFRESH_SELECT_OPTIONS':
      return refreshSelectOptions(state, action.view)
    case 'RESET':
      // Return initial state to release all row data references for garbage collection
      return action.initialState
    case 'DELETE_TABLE':
      return {
        ...state,
        tableDetails: { ...state.tableDetails, isDeleted: true }
      }
    default:
      return state
  }
}
